import type { IFilesystem } from '@livx.cc/wcli/core';
import type { RouteDecision } from './modelRouter';
import type { ChatLike, ChatResponse, Message, StreamChunk, ToolCall, MessageContent, ContentPart } from './llm';
import { contentText, imagePart, elideStaleImages, elideImages, messageHasImage, expandImagesForSend, sendBytes, sendBytesPer, endsOnAnnouncedAction, passesBackReasoning } from './llm';
import { type AgentTool, type ToolContext, type HostBridge, type UserQuestion, defaultTools, makeContext, toWireTools, truncateOutput } from './tools';
import { JobRegistry, makeJobTools, formatJobNotice, type JobHandle } from './tools.jobs';
import { askUserQuestionTool } from './host';
import { loadSkills, scanSkills } from './skills';
import { loadCommands, scanCommands } from './commands';
import { loadMemory } from './memory';
import { loadInstructions } from './instructions';
import { makeTaskTool, makeTaskBatchTool } from './subagent';
import { modelProvider } from './models';
import { loadAgents } from './agents';
import { checkpointTools } from './OverlayFilesystem';
import type { Hooks } from './hooks';
import { PermissionPolicy, planMode, composeHooks } from './permissions';
import { forComponent } from './logging';
import { checkSyntax } from './lint';
import { reasoningToChatFragment, type ReasoningEffort } from './reasoning';

const log = forComponent('Agent');

/** Read a non-negative integer env override, guarded for non-node (edge/browser) builds. */
function envInt(key: string, fallback: number): number {
  const n = typeof process !== 'undefined' ? Number(process.env?.[key]) : NaN;
  return Number.isFinite(n) && n >= 0 ? n : fallback;
}

/** A browser-bridge MCP tool name (`mcp__<server>__<tool>` where the server name mentions "browser").
 *  These return huge DOM/screenshot payloads — their USE is the signal that triggers adaptive trimming. */
function isBrowserTool(name: string): boolean {
  return name.startsWith('mcp__') && /browser/i.test(name.slice(5).split('__')[0]);
}

/** A thrown provider error that is really a cancel (barge-in / Ctrl-C), not a crash. The abort flag can
 *  lag the throw under a race, so we message-match across providers: fetch AbortError, Node ABORT_ERR,
 *  groq/SDK "operation was aborted", ConnectError [canceled]. Mirrors the CLI's cursor-abort guard. */
function isAbortError(err: unknown): boolean {
  const e = err as any;
  const blob = `${e?.message ?? ''} ${e?.name ?? ''} ${e?.code ?? ''} ${e?.cause?.name ?? ''}`;
  return /operation was aborted|\bAbortError\b|ABORT_ERR|\[canceled\]/i.test(blob);
}

export interface RunResult {
  text: string;
  steps: number;
  /** Why the loop ended. The middle group are automatic kill-switches (budget/abuse guards).
   *  `needs_input` is NOT a failure: the agent hit a decision that was the user's to make with no
   *  human reachable, so it stopped and raised the question (see `question`) instead of guessing.
   *  Answer it and resume the session to continue. */
  finishReason: 'stop' | 'max_steps' | 'budget' | 'timeout' | 'loop' | 'max_tool_calls' | 'aborted' | 'error' | 'needs_input' | 'empty';
  messages: Message[];
  /** The provider's own finish reason for the final model call (e.g. 'stop', 'end_turn', 'length'), when it gave one. */
  providerFinishReason?: string;
  /** The parked question — present iff `finishReason === 'needs_input'`. */
  question?: UserQuestion;
  /** Accumulated token usage across all turns (non-stream path). With prompt caching,
   *  promptTokens includes cached reads/writes; the cache splits ride along for exact pricing. */
  usage?: { promptTokens: number; completionTokens: number; totalTokens: number; cacheCreationTokens?: number; cacheReadTokens?: number };
  /** True if ANY turn's usage was estimated (provider gave none) rather than exact — lets the UI mark cost `~`. */
  usageEstimated?: boolean;
  /** Set by a router (`--model auto`) whose turn spans several models: the turn's USD at each model's own rate. */
  costUsd?: number;
  /** `--model auto` routing decision for this turn. */
  routing?: RouteDecision;
  /** Advertised tools the model NAMED in the reply it is handing back while the run's invocation ledger
   *  shows zero calls to them — a fabricated tool use, proven against ground truth rather than guessed
   *  from phrasing. A host can use it to tell the user plainly instead of shipping the invented result.
   *
   *  Re-evaluated at RETURN time, not frozen at detection: a claim the recovery nudge rescued (the model
   *  went and made the real call) is dropped. It used to survive, on the reading that the field recorded
   *  "this turn needed rescuing" — but the only consumer turns it into a sentence for the user ("I never
   *  ran it"), and on the guard's own success path that sentence is false. Whether a turn needed rescuing
   *  is a telemetry question, and the `log.warn` at the nudge already answers it. */
  fabricatedToolClaims?: string[];
  error?: unknown;
}

/** What a manual `/compact` actually freed (token estimates, ~4 chars/token). */
export interface CompactResult { tokensBefore: number; tokensAfter: number; target: number; cap: number; removed: number }

export class AgentOptions {
  /** Any ai.libx.js AIClient (or a FakeAIClient). */
  ai!: ChatLike;
  /** Filesystem backend — MemFilesystem, NodeDiskFilesystem, IndexedDbFilesystem, …
   *  OPTIONAL: if omitted, the agent lazily defaults to a JAILED real disk rooted at `process.cwd()`
   *  (secrets hidden by DEFAULT_DENY) — resolved via a dynamic node import at first run, so the edge/
   *  browser build (which always passes its own fs) never pulls in `node:fs`. Pass `fs` explicitly to
   *  use any other backend (and to keep full control of the jail). */
  fs?: IFilesystem;
  model = 'anthropic/claude-sonnet-4-6';
  systemPrompt =
    'You are a coding agent operating on a virtual filesystem. Use the `bash` tool to explore (ls, grep, find, cat) and the `Read`/`Edit` tools to view and modify files. Always Read a file before Editing it — and before overwriting it with `Write` or a whole-file `ApplyEdits`: Write replaces the ENTIRE file, so blindly writing over an existing one destroys its unseen contents. If a file already exists, Read it first, then merge your changes (or Edit the specific part). When multiple tool calls are independent (e.g. several Reads or Greps with no dependency between them), issue them together in a single turn rather than one at a time. When the task is complete, reply with a short summary and make no further tool calls.\n' +
    'Recovery: every turn\'s file changes are checkpointed. If the user asks to undo/revert/recover edits (including a file you overwrote), tell them to run `/rewind` (or `/undo` for the last turn) to restore the working tree — checkpoints cover files you edited even in .gitignored dirs. You cannot run `/rewind` yourself; surface it as the recovery path.\n' +
    'When working with a knowledge base or document workspace: use `RepoMap` with scope "docs" to see all document headings and summaries (orient first); use `MemorySearch` to find memories by content when you don\'t know the exact slug; use `Recall` with multiple slugs to batch-load related facts in one call. Prefer targeted loads over reading everything — orient → search → load → act.';
  tools: AgentTool[] = defaultTools();
  maxSteps = 25;
  // --- automatic kill-switches (always on; protect the API budget against runaway loops/abuse) ---
  /** Hard ceiling on accumulated tokens (prompt+completion) across the run; cache READS count at 0.1×
   *  (their real price) so a fat cached context doesn't trip the guard on healthy turns. 0 = unbounded. */
  maxTokens = 200_000;
  /** Idle ceiling in ms: max time since the last productive step before the run is killed (a long/active
   *  step resets it — see runLoop's `lastProgressAt`). Not a whole-run wall clock. 0 = unbounded. */
  timeoutMs = 300_000;
  /** Idle-stall watchdog for a SINGLE streamed model request: abort it if no chunk (text OR reasoning)
   *  arrives for this long. The between-steps `timeoutMs` can't preempt an in-flight `chat()`, so a
   *  provider that goes silent mid-stream would otherwise park the loop forever. 0 = off. */
  stallMs = 60_000;
  /** IDLE bound on a NATIVE delegated tool's hold over the stall watchdog (cursor's own shell/mcp/task:
   *  `running` → silence → `completed`). Such a hold is the provider's own unverified CLAIM that it is
   *  busy, and it SUSPENDS the very stall detection that would catch that provider going silent — so its
   *  only backstop was `hardCapMs = toolHoldMs`, the TRANSPORT deadline (≥30m, sized for host-tool
   *  round-trips a native call never makes). A wedged native call therefore bought 30m of total silence,
   *  ×2 attempts = the 61-minute zero-output run (blank 2026-09-18). Idle, not absolute: every further
   *  `running` event `touch()`es the job, so a native tool that keeps reporting progress holds
   *  indefinitely — what expires is silence, not work. 0 = off (hard cap only). */
  nativeHoldMs = 300_000;
  /** Stop if the identical tool-call batch (name+args) repeats this many times in a row. 0 = off. */
  maxRepeats = 3;
  /** Cumulative cap on tool calls dispatched across the run. 0 = unbounded. */
  maxToolCalls = 100;
  /** External cancellation — abort the loop between steps (e.g. a UI "Cancel" button). */
  signal?: AbortSignal;
  /** Retry budget for an EMPTY/silent-turn (a delegated-runtime wedge: cursor's warm helper spins then
   *  returns a content-less end, surfaced as `code:'empty'`/`code:'cursor_silent'`). Tuned WIDER than the
   *  network-drop budget (fixed 2) because clearing a wedge needs several reap+respawn cycles — each retry
   *  hits a FRESH cursor session (the adapter evicts the wedged warm helper on the silent-end; a stall-watchdog
   *  cancel is NOT this class — it is classified `stall`, and the watchdog's coded abort reason is what tells
   *  the adapter to evict on that path too). Still bounded:
   *  after exhaustion the turn surfaces `finishReason:'empty'` (non-zero exit), never infinite, never silent.
   *  Env: AGENTX_EMPTY_RETRIES. */
  emptyRetries = envInt('AGENTX_EMPTY_RETRIES', 4);
  /** Base backoff (ms) between empty-turn retries; grows linearly per attempt (longer than the network
   *  class's fixed 1s so a wedged helper has time to be reaped/respawned). Env: AGENTX_EMPTY_RETRY_BASE_MS. */
  emptyRetryBaseMs = envInt('AGENTX_EMPTY_RETRY_BASE_MS', 1500);
  /** 0 = never trim. Otherwise cap the messages sent per turn (system + most recent). */
  maxContextMessages = 0;
  /** Note-taking: keep the most-recent N tool-result outputs verbatim; collapse OLDER ones to a one-line
   *  stub in the sent context (the model already consumed them — it can re-Read/re-run). The stored
   *  transcript is never mutated. 0 = keep all verbatim. */
  keepToolOutputs = 0;
  /** How many of the most-recent image-bearing messages get their image REF expanded to real base64 in
   *  the outgoing request. Older ones (and legacy inline base64) go on the wire as the elision stub. */
  keepRecentImages = 1;
  /** Token-aware backstop (~4 chars/token estimate). After note-taking, drop oldest messages from the
   *  sent context until the estimate is under this ceiling (pairing-safe). 0 = off. */
  maxContextTokens = 0;
  /** The MODEL's context window (tokens) — what the context % is measured against and what `/compact`
   *  targets 50% of. Filled by the CLI from model metadata; 0 = fall back to 200k. Deliberately NOT
   *  `maxTokens`: that is the cumulative SPEND kill-switch, and a spend budget must never be able to
   *  drive destructive compaction (`--max-tokens 50000` would otherwise shred the transcript). */
  contextWindow = 0;
  /** Pagination ceiling for a SINGLE tool result (bytes). A result over this is cropped to page 1 with
   *  a marker telling the model it was cropped (refine the query, or page further). Guards against one
   *  Grep/Read/MCP call blowing the whole context window. 0 = off. Default 60k (~15k tokens). */
  maxToolResultBytes = 60_000;
  /** Hook to handle an oversized tool result instead of the default lossy crop: receives the FULL output
   *  and returns the (cropped) string to put in context — e.g. spill to scratch and return a recoverable,
   *  paginated stub. Called only when a result exceeds `maxToolResultBytes`. */
  capToolResult?: (full: string, info: { tool: string; args: any }) => string | Promise<string>;
  /** Browser-run adaptive trimming. Browser MCP tools (`mcp__*browser*`) return huge per-call results
   *  (full DOM text + screenshots) that accumulate per step and blow the cumulative `maxTokens` budget
   *  kill-switch mid-browse. The fix attacks PER-STEP TOKEN GROWTH, and engages ONLY once a browser tool
   *  is ACTUALLY invoked (not on mere config presence — so normal coding is untouched):
   *   - `resultBytes`: tighter `maxToolResultBytes` applied to BROWSER tool results (each oversized DOM is
   *     cropped/spilled to a small stub → small fresh-token tail per step).
   *   - `keepOutputs`: keep only the most-recent N tool-result bodies verbatim ONCE a browser tool has run
   *     (older huge DOMs collapse to one-line stubs → small cached prefix → small 0.1×cacheRead/step).
   *  Explicit `keepToolOutputs`/`maxToolResultBytes` are NOT overridden below their browser values — the
   *  tighter of (user, browser) wins, never loosening a user's cap. Unset = off (no adaptive behavior). */
  browserTrim?: { resultBytes?: number; keepOutputs?: number };
  /** VFS dir(s) of skills (`<dir>/<id>/SKILL.md`). If set: inject a catalog + add the `Skill` tool. Multiple dirs are merged (first wins on name collisions). */
  skillsDir?: string | string[];
  /** VFS dir(s) of slash-command templates (`<dir>/<name>.md`). If set: inject a catalog + add the `SlashCommand` tool. Multiple dirs are merged (first wins). */
  commandsDir?: string | string[];
  /** VFS dir(s) of memory (`<dir>/MEMORY.md`). If set: inject the index at run start (persistence = backend).
   *  Multiple dirs are merged (reads search all; writes go to first). */
  memoryDir?: string | string[];
  /** User-scope memory dir for global facts (type=user/feedback). Remember routes by type when set. */
  memoryUserDir?: string;
  /** Filenames to discover as project instructions (e.g. `AGENT.md`, `AGENTS.md`, `CLAUDE.md`).
   *  Walks the VFS tree and merges all found files (general → specific, like Claude Code).
   *  `true` (default) = auto-discover standard names. `string[]` = custom names. `false` = skip. */
  instructionFiles: boolean | string[] = true;
  /** Host interaction channel (human-in-the-loop). If set: adds the `AskUserQuestion` tool. */
  host?: HostBridge;
  /** Add the `AskUserQuestion` tool when a host is present (default true). Set false for an agent that
   *  must never block a turn on a structured question — e.g. a voice reflex that confirms inline. */
  askUserQuestion = true;
  /** Deterministic interception points around tool execution (pre/post/stop). */
  hooks?: Hooks;
  /** If true: add the `Task` tool so the agent can spawn depth-limited child agents over the VFS. */
  subagents = false;
  /** VFS dir of typed-subagent defs (`<dir>/<name>.md`). If set with `subagents`: inject a catalog + enable the `Task` `agentType` param. */
  agentsDir?: string;
  /** Current recursion depth (0 = top-level); spawned children run at depth+1. */
  depth = 0;
  /** Hard ceiling on subagent nesting (beyond it the `Task` tool refuses to spawn). */
  maxDepth = 2;
  /** Real-disk dir where child agents stream their transcript as they run (origin-anchored — survives
   *  worktree teardown / a child's own cwd). When set, an interrupted/failed child commits a STATUS HINT
   *  + a pointer to its trace instead of a dangling tool_call, so the parent can read it and decide
   *  whether to ignore, pick it up, or restart. Unset (library/sandbox default) = no tracing. */
  traceDir?: string;
  /** Stream tokens from the model. Takes effect only with a `host.notify`; off => current (non-stream) behavior. */
  stream = false;
  /** Closure reflex for delegated runtimes (cursor/claude-code): when such a turn runs tools but never
   *  closes — no assistant text after the last tool, or text that trails off on an action it announced
   *  and never took ("Also checking for X.") — nudge ONE more step to ANSWER the user
   *  from the tool results already in the transcript — so the turn delivers a reply instead of trailing off
   *  mid-narration. Deliberately not a "summarize what you did" nudge: that produced process reports and
   *  permission-asking instead of answers (see the nudge text in runLoop).
   *  Fires at most once per run and only for delegated runtimes (native turns never emit the trigger). */
  closeDelegatedTurns = true;
  /** Catch a FABRICATED tool use: a turn that emits no tool call at all, yet whose text names an advertised
   *  tool this run has never actually invoked — the model announcing `look_at_app` and then writing its own
   *  "it didn't respond in time" result. No call-reactive layer can see it (there IS no call), and to every
   *  surface it is indistinguishable from a real failure. The discriminator is the per-run invocation ledger,
   *  not prose: a named tool with zero invocations is provable. On detection the loop nudges ONCE for a real
   *  call, and records the names in `RunResult.fabricatedToolClaims` either way. */
  catchFabricatedToolUse = true;
  /** An empty model turn (no text, no tools) that ends the loop after work is re-prompted ONCE, then finishes
   *  as `finishReason:'empty'` (a failure) instead of a phantom 'stop'. Off for layers that deliberately
   *  allow a silent turn and repair it themselves (the duplex voice reflex). */
  failEmptyTurns = true;
  /** Fold the dropped middle of an over-long transcript into a synthetic summary (edge-safe, no LLM). Off => drop-oldest. */
  compaction?: { maxMessages: number };
  /** Add `Checkpoint`/`Rollback` tools (requires the fs to be an OverlayFilesystem). */
  checkpoints = false;
  /** Enable `bash({background:true})` + JobOutput/JobStatus/JobKill — sandbox background jobs (overlay-isolated,
   *  committed on completion, drained at turn end). Useful when the VFS backend is slow (remote) or for sub-agents. */
  backgroundJobs = false;
  /** The job registry every long-lived tool call lives in (MCP calls past their soft timeout, delegated host/native
   *  tools, and — when the host shares it with its `ShellJobRegistry` — shell jobs). Its liveness is what the stall
   *  watchdog and the stuck-job reaper decide from. Default: a fresh registry per Agent. Knobs (idle windows, hard
   *  caps, MCP soft timeout) live on `JobRegistryOptions`. Completion notices reach the model via `inject` unless
   *  the host set its own `onExit`. */
  jobs?: JobRegistry;
  /** Plan mode: block mutating tools until the agent calls `ExitPlanMode` (host-approved). */
  planMode = false;
  /** Permission policy gating each tool call (allow / ask / deny). */
  permissions?: PermissionPolicy;
  /** Opt-in syntax guardrail: refuse to persist a syntactically-broken code-file write/edit. Default off. */
  lintOnWrite?: boolean;
  /** Optional PDF text extraction for Read on .pdf files (node hosts wire pdftotext); absent => Read explains. */
  pdfText?: (path: string) => Promise<string>;
  /** Opt-in: after a write-class tool runs, run `command` over the VFS and append any failure to the tool result.
   *  `tools` defaults to ['Write','Edit','MultiEdit','ApplyEdits']. */
  autoTest?: { command: string; tools?: string[] };
  /** Provider-specific options forwarded to ai.chat() (e.g. cursor mcpServers, cwd). */
  providerOptions?: Record<string, unknown>;
  /** Derive `providerOptions` for a DELEGATED child whose model may differ from this agent's
   *  (`Task({ model })` / an agentType def). Default: inherit `providerOptions` only when the child's
   *  model is on the SAME provider — cursor's `cwd`/`cursorSession` on an anthropic child is a 400. */
  providerOptionsFor?: (model: string) => Record<string, unknown> | undefined;
  /** Prompt caching (providers that support it, e.g. Anthropic): cache tools/system/conversation
   *  prefix across the loop's steps — reads cost 0.1x, writes 1.25x. A multi-step agent loop
   *  re-sends its whole prefix every step, so this is a large net cost cut. Default on. */
  promptCache = true;
  /** Tool selection mode: 'auto' = model decides (needed for Groq); undefined = provider default. */
  toolChoice?: 'auto' | 'required' | 'none';
  /** Extended-thinking / reasoning effort, normalized across providers (anthropic, openai).
   *  `'off'`/undefined = none; `'low'|'medium'|'high'` or a raw token budget. Mapped to the
   *  provider-specific request shape via {@link reasoningToChatFragment}; explicit `providerOptions` wins. */
  reasoning?: ReasoningEffort;
  /** Seed a fresh `run()` with prior conversation turns (real role/content messages) so the model
   *  sees history as structured turns instead of a flattened text blob. They are inserted AFTER the
   *  stable system prompt and BEFORE the current user turn — so the cached system(+tools) prefix never
   *  shifts. Turns are normalized before sending (empties dropped, adjacent same-role coalesced, and the
   *  first tail turn forced to `user`) so the transcript is always a valid Anthropic alternation ending
   *  at the current user prompt. Unset/empty ⇒ current behavior, byte-for-byte. */
  priorMessages?: Array<{ role: 'user' | 'assistant'; content: MessageContent }>;
}

/** Normalize seed history + the live prompt into a valid alternating tail (the messages that follow the
 *  system turn). Drops empties, coalesces adjacent same-role turns, guarantees the first turn is `user`
 *  (Anthropic requires the first non-system message to be user) and that the sequence ends at `current`.
 *  Empty `prior` ⇒ `[{ user: current }]` — identical to the pre-priorMessages transcript. */
function seedTranscriptTail(
  prior: Array<{ role: 'user' | 'assistant'; content: MessageContent }> | undefined,
  current: MessageContent,
): Message[] {
  const seq: Message[] = (prior ?? [])
    .filter((m) => contentText(m.content).trim().length > 0)
    .map((m) => ({ role: m.role, content: m.content }));
  seq.push({ role: 'user', content: current });
  const out: Message[] = [];
  for (const m of seq) {
    const last = out[out.length - 1];
    if (last && last.role === m.role) {
      if (typeof last.content === 'string' && typeof m.content === 'string') {
        last.content = `${last.content}\n\n${m.content}`; // merge same-role text turns
        continue;
      }
      // multimodal boundary (can't concat) — insert a neutral opposite-role spacer to keep alternation
      out.push({ role: m.role === 'user' ? 'assistant' : 'user', content: '(continuing)' });
    }
    out.push({ role: m.role, content: m.content });
  }
  if (out.length && out[0].role !== 'user') out.unshift({ role: 'user', content: '(Earlier conversation)' });
  return out;
}

/**
 * The agentic loop: chat() -> dispatch tool_calls over the VFS -> thread results
 * back -> repeat until the model stops (no tool calls) or maxSteps is hit.
 */
export class Agent {
  public options: AgentOptions;
  public transcript: Message[] = [];
  private ctx!: ToolContext; // built in the ctor when `fs` is provided, else lazily in ensureFs()
  private lastTrimNotified = 0; // last auto-trim drop count surfaced via host.notify (dedup)
  private activeTools: AgentTool[] = [];
  /** Per-run ground truth: every tool name actually INVOKED this run (native dispatch + delegated
   *  runtime activity). The ledger that makes a fabricated tool claim provable. Reset per run. */
  private invokedTools = new Set<string>();
  /** Every long-lived tool call is a job here (see AgentOptions.jobs). Delegated-runtime HOST tools (cursor
   *  toolExecutor) and NATIVE tools (cursor's own shell/mcp/task, `running` → `completed`) are tracked as
   *  `holds` jobs: while one runs the provider is not silent — it is waiting on a tool — so the idle-stall
   *  watchdog must not fire. Each is bounded by the transport tool deadline (`hardCapMs = toolHoldMs`), so a
   *  tool that never settles (or a lost `completed` event) is reaped and cannot disable stall protection.
   *  A NATIVE job carries a second, much tighter bound (`idleMs = nativeHoldMs`): the transport deadline is
   *  sized for a host-tool round-trip it never makes, and silence is the only thing it can actually prove. */
  readonly jobs: JobRegistry;
  /** Native delegated tool activity id → its job (per step attempt). */
  private nativeJobs = new Map<string, JobHandle>();
  private toolHoldMs = 0;
  private nativeHoldMs = 0;
  /** A tool (host or native) already executed in the current step attempt: a stall retry replays the step
   *  from the pre-step transcript, which would re-run it — so such a stall is not retried. */
  private toolRanThisAttempt = false;
  /** Resets the active request's stall timer; a host tool's completion restarts the model's idle window. */
  private stallPoke?: () => void;
  /** Advertised tools this run's text claimed to have used while the ledger showed zero calls. */
  private fabricatedClaims = new Set<string>();
  private activeHooks?: Hooks; // composed: user hooks + plan-mode + permissions
  private prepared = false;        // memo guard: prompt/tools/plan-state built once per conversation
  private systemPromptCache = '';  // the assembled system prompt from the last prepare()
  private started = false;         // session-start lifecycle hook fires once per conversation
  private parkedMs = 0;            // cumulative time blocked on the HUMAN (permission/plan prompts) — excluded from the timeout
  private browserActive = false;   // flips true once a browser MCP tool is actually invoked → adaptive trimming engages (browserTrim)

  /** Time a human-blocking await (a permission/plan prompt) and bank it in `parkedMs` so idle prompt
   *  time never trips the wall-clock kill-switch. The agent did no work while parked on the user. */
  private async park<T>(p: Promise<T>): Promise<T> {
    const t = Date.now();
    try { return await p; } finally { this.parkedMs += Date.now() - t; }
  }

  /** Force the next `send()`/`run()` to rebuild the system prompt, tools, plan-mode and permission hooks
   *  from `options` — apply mid-conversation changes to `planMode`/`permissions`/`model` etc. (prepare()
   *  is otherwise memoized per conversation). */
  reprepare(): void { this.prepared = false; }

  /** Tools injected via addTools(); kept separate from options.tools so prepare() rebuilds don't drop them. */
  private injectedTools: AgentTool[] = [];

  /** Current tool surface for hosts/UIs (configured + injected, deduped by name) — options.tools
   *  alone misses addTools()-injected ones (e.g. MCP servers mounted after startup). */
  get toolNames(): string[] {
    return [...new Set([...(this.options.tools ?? []), ...this.injectedTools].map((t) => t.name))];
  }

  /** Inject tools into a running agent (e.g. dynamically mounted MCP servers). Takes effect on the next turn
   *  and survives prepare() rebuilds (reprepare(), new conversations). */
  addTools(tools: AgentTool[]): void {
    this.injectedTools.push(...tools);
    this.activeTools.push(...tools);
    // MCP mounted after startup can hand calls off to jobs — the model needs the tools to follow them.
    if (this.prepared && tools.some(isMcpTool) && !this.activeTools.some((t) => t.name === 'JobWait')) this.activeTools.push(...makeJobTools(this.jobs));
  }

  /** Remove tools by name from a running agent. Returns the count removed. Also filters
   *  `options.tools` — otherwise a constructor-passed tool resurrects on the next prepare()
   *  rebuild (and re-adding it via addTools() would then DUPLICATE it in the wire schema). */
  removeTools(names: Set<string> | string[]): number {
    const s = names instanceof Set ? names : new Set(names);
    const before = this.activeTools.length;
    this.activeTools = this.activeTools.filter((t) => !s.has(t.name));
    this.injectedTools = this.injectedTools.filter((t) => !s.has(t.name));
    if (this.options.tools) this.options.tools = this.options.tools.filter((t) => !s.has(t.name));
    return before - this.activeTools.length;
  }

  /** Messages the host queued to steer the RUNNING turn (Claude-Code-style mid-turn input). The loop
   *  drains them at each step boundary — after tool results are appended, before the next model call —
   *  so they ride along with the latest tool results. A no-op between turns (nothing drains it). */
  private pendingInjections: MessageContent[] = [];

  /** Queue a user message into the in-flight turn. It lands as a `user` message at the next step
   *  boundary; if the model was about to stop, the injection re-opens the loop so it's never lost.
   *  Pushing onto `transcript` directly from outside would race the loop — this drains inside it. */
  inject(content: MessageContent): void { this.pendingInjections.push(content); }

  /** Drop any queued-but-unconsumed injections. The REPL calls this after a LOCAL command (/cmd, !bash)
   *  — which runs no send() to drain them — so a mid-command steer can't leak into the next real turn. */
  clearInjections(): void { this.pendingInjections.length = 0; }

  /** Pull queued injections that are plain TEXT, removing them, for a runtime that can accept mid-turn
   *  steers over its own channel (claude-code's warm InputQueue via `providerOptions.steer`). Non-text
   *  injections (images) stay queued for the step-boundary `drainInjections()` re-open — the text-only
   *  steer channel can't carry them. Consumed DURING chat(); no overlap with drainInjections() (post-call). */
  drainPendingSteers(): string[] {
    if (!this.pendingInjections.length) return [];
    const texts: string[] = [];
    this.pendingInjections = this.pendingInjections.filter((c) => {
      if (typeof c === 'string') { texts.push(c); return false; }
      return true; // keep non-text (image parts) for the transcript-injection path
    });
    return texts;
  }

  /** Fold any host-injected messages into the transcript as user turns. Returns true if any landed. */
  private drainInjections(): boolean {
    if (!this.pendingInjections.length) return false;
    for (const content of this.pendingInjections.splice(0)) this.transcript.push({ role: 'user', content });
    return true;
  }

  constructor(options?: Partial<AgentOptions>) {
    this.options = { ...new AgentOptions(), ...options } as AgentOptions;
    this.jobs = this.options.jobs ?? new JobRegistry();
    // A job that finishes after its caller stopped waiting (MCP handoff) or is reaped as stuck must reach the MODEL.
    this.jobs.options.onExit ??= (n) => {
      // A handed-off MCP result may carry images (a render): deliver them with the notice, not just "[1 image(s)]".
      const imgs = (n.result as { images?: { mimeType: string; data: string }[] } | undefined)?.images;
      this.inject(imgs?.length ? [{ type: 'text', text: formatJobNotice(n) }, ...imgs.map((i) => imagePart(`data:${i.mimeType};base64,${i.data}`))] : formatJobNotice(n));
    };
    if (this.options.fs) this.buildCtx(); // fs provided → build the tool context now (sync, unchanged behavior)
  }

  /** Build the tool context from the resolved fs + options. Idempotent-safe: called once (ctor or ensureFs). */
  private buildCtx(): void {
    this.ctx = makeContext(this.options.fs!, this.options.host);
    this.ctx.signal = this.options.signal; // expose run-cancellation to abort-aware tools (e.g. real shell)
    if (this.options.lintOnWrite) this.ctx.lint = checkSyntax; // opt-in syntax guardrail
    if (this.options.pdfText) this.ctx.pdfText = this.options.pdfText; // host-wired PDF text extraction (Read on .pdf)
    this.ctx.ai = this.options.ai; // expose model to tools that run their own LLM pass (e.g. Review)
    this.ctx.model = this.options.model;
    this.ctx.parkHuman = (p) => this.park(p); // route interactive tools' human-waits into the parked-time accumulator
    this.ctx.toolJobs = this.jobs; // MCP soft-timeout handoff etc.
    if (this.options.backgroundJobs) this.ctx.jobs = this.jobs; // enables bash({background:true})
  }

  /**
   * Resolve the filesystem + build the tool context if the ctor couldn't (no `fs` was passed). The disk
   * default lives HERE, not in the ctor: the ctor can't await, and we must avoid a STATIC `node:fs` import
   * in the core (edge/browser would break). The dynamic import only fires when `fs` is omitted — edge code
   * always passes its own `MemFilesystem`, so the node module is never reached. Default = jailed disk @ cwd.
   */
  private async ensureFs(): Promise<void> {
    if (this.ctx) return; // already built (fs was provided to the ctor, or a prior run resolved it)
    if (!this.options.fs) {
      const { NodeDiskFilesystem } = await import('./NodeDiskFilesystem');
      const { JailedFilesystem } = await import('./JailedFilesystem');
      const disk = new NodeDiskFilesystem(process.cwd());
      await disk.init();
      this.options.fs = new JailedFilesystem(disk); // DEFAULT_DENY hides secrets out of the box
      log.info(`no fs provided — defaulting to jailed real disk at ${process.cwd()}`);
    }
    this.buildCtx();
  }

  /**
   * Assemble the system prompt + active tools/hooks ONCE per conversation (memoized).
   * `run()` resets the memo to start fresh; `send()` reuses it, so multi-turn state —
   * plan-mode approval, the tool set, the skills/commands/memory snapshot — stays stable
   * across turns (and the frozen prompt pairs well with prompt caching). Does NOT touch
   * the transcript; `run`/`send` own that.
   */
  private async prepare(taskHint?: string): Promise<string> {
    if (this.prepared) return this.systemPromptCache;
    const o = this.options;
    const fs = o.fs!; // resolved by ensureFs() before any run()/send() reaches prepare()
    let systemPrompt = o.systemPrompt;
    let tools = o.tools;
    if (o.instructionFiles !== false) {
      const names = Array.isArray(o.instructionFiles) ? o.instructionFiles : undefined;
      const ins = await loadInstructions(fs, names);
      if (ins) systemPrompt += '\n\n' + ins;
    }
    if (o.memoryDir) {
      // taskHint floats the most task-relevant learnings to the top of a large index; the rest
      // collapse to slugs (still Recall-able) — bounds tokens as learnings accumulate over time.
      const { index, tools: memTools } = await loadMemory(fs, o.memoryDir, { relevanceHint: taskHint, userDir: o.memoryUserDir });
      if (index) systemPrompt += '\n\n' + index; // hot: compact (ranked) index always injected
      tools = [...tools, ...memTools]; // cold: Recall (pull fact) + Remember (persist fact)
    }
    // Merged slash namespace (CC parity): each tool cross-resolves into the other registry on a
    // miss, so `/x` works whether x is a command or a skill regardless of which tool the model picks.
    const skillsDir = o.skillsDir, commandsDir = o.commandsDir;
    if (skillsDir) {
      // taskHint floats the most relevant skills to the top of a large catalog; full set stays loadable by name.
      const sibling = commandsDir
        ? async (name: string) => {
            const c = (await scanCommands(fs, commandsDir)).find((x) => x.name === name);
            return c ? { path: c.path, stripFrontmatter: true } : undefined;
          }
        : undefined;
      const { catalog, tool } = await loadSkills(fs, skillsDir, { relevanceHint: taskHint, sibling });
      if (catalog) systemPrompt += '\n\n' + catalog;
      if (tool) tools = [...tools, tool];
    }
    if (commandsDir) {
      const sibling = skillsDir
        ? async (name: string) => {
            const s = (await scanSkills(fs, skillsDir)).find((x) => x.name === name);
            return s ? { path: s.path, stripFrontmatter: false } : undefined;
          }
        : undefined;
      const { catalog, tool } = await loadCommands(fs, commandsDir, { relevanceHint: taskHint, sibling });
      if (catalog) systemPrompt += '\n\n' + catalog;
      if (tool) tools = [...tools, tool];
    }
    if (o.host && o.askUserQuestion) tools = [...tools, askUserQuestionTool];
    if (o.subagents) {
      let agents: Awaited<ReturnType<typeof loadAgents>>['agents'] | undefined;
      if (o.agentsDir) {
        const loaded = await loadAgents(fs, o.agentsDir);
        agents = loaded.agents;
        if (loaded.catalog) systemPrompt += '\n\n' + loaded.catalog;
      }
      // The child inherits the HOST-configured toolbelt (o.tools + addTools) and system prompt — not the
      // derived memory/skills/commands tools, which are closed over THIS agent's fs (they'd keep writing
      // to THIS agent's stores no matter where the child runs). Without this a child fell back to jailed
      // defaultTools(). Inherited tools that escape the VFS are re-bound per child: `subagent.ts` calls
      // `AgentTool.withCwd` so a worktree child's real Shell runs in the WORKTREE, not the parent repo.
      // An overlay child's Shell cannot be isolated at all (an overlay is a VFS layer, not a process
      // jail) — the Task/TaskBatch descriptions say so instead of promising isolation we can't keep.
      const taskOpts = {
        ai: o.ai, model: o.model, fs, depth: o.depth, maxDepth: o.maxDepth, agents, hooks: o.hooks, traceDir: o.traceDir,
        tools: [...o.tools, ...this.injectedTools],
        systemPrompt: o.systemPrompt,
        providerOptionsFor: o.providerOptionsFor ?? ((m: string) => (modelProvider(m) === modelProvider(o.model) ? o.providerOptions : undefined)),
      };
      tools = [...tools, makeTaskTool(taskOpts), makeTaskBatchTool(taskOpts)];
    }
    if (o.checkpoints) tools = [...tools, ...checkpointTools()];
    // JobOutput/JobStatus/JobKill/JobWait: with sandbox background jobs, or whenever an MCP tool can hand a call off to a job.
    if (this.ctx.jobs || [...tools, ...this.injectedTools].some(isMcpTool)) tools = [...tools, ...makeJobTools(this.jobs)];
    const plan = o.planMode ? planMode({ host: o.host }) : undefined; // gates mutating tools until approved
    if (plan) tools = [...tools, plan.tool];
    this.activeHooks = composeHooks(o.hooks, plan?.hooks, o.permissions?.hooks());
    this.activeTools = [...tools, ...this.injectedTools]; // injected (addTools) survive the rebuild
    this.systemPromptCache = systemPrompt;
    this.prepared = true;
    return systemPrompt;
  }

  /** Single-shot: reset all per-conversation state and run `task` to completion. `task` may be plain
   *  text or multimodal content parts (text + images). */
  async run(task: MessageContent): Promise<RunResult> {
    await this.ensureFs();  // resolve the disk default (if no fs was passed) before prepare reads o.fs
    this.prepared = false; // fresh conversation → rebuild prompt + tools + plan-mode state
    this.started = false;
    this.invokedTools.clear();   // fresh conversation → fresh ground truth (a `send()` continuation KEEPS the
    this.fabricatedClaims.clear(); // ledger on purpose: naming a tool it really called last turn is honest)
    const systemPrompt = await this.prepare(contentText(task)); // task text hints catalog relevance
    const startCtx = await this.fireSessionStart();      // [lifecycle] once-per-session context injection
    const userContent = await this.applyPromptSubmit(task); // [lifecycle] per-turn prompt rewrite (text only)
    this.transcript = [
      { role: 'system', content: systemPrompt + (startCtx ? '\n\n' + startCtx : '') },
      // Prior turns (if any) sit between the stable system prefix and the live user turn — the cached
      // system(+tools) prefix is unaffected; normalization keeps the alternation valid.
      ...seedTranscriptTail(this.options.priorMessages, userContent),
    ];
    return this.runLoop();
  }

  /** Apply onUserPromptSubmit to a turn: rewrite plain text; pass multimodal content through untouched. */
  private async applyPromptSubmit(task: MessageContent): Promise<MessageContent> {
    return typeof task === 'string' ? await this.fireUserPromptSubmit(task) : task;
  }

  /** Fire onSessionStart once per conversation; returns any injected context. */
  private async fireSessionStart(): Promise<string | undefined> {
    if (this.started) return undefined;
    this.started = true;
    const ctx = await this.activeHooks?.onSessionStart?.();
    return typeof ctx === 'string' && ctx ? ctx : undefined;
  }

  /** Fire onUserPromptSubmit; returns the (possibly rewritten) prompt text. */
  private async fireUserPromptSubmit(task: string): Promise<string> {
    const r = await this.activeHooks?.onUserPromptSubmit?.(task);
    return typeof r === 'string' ? r : task;
  }

  /**
   * Multi-turn: continue the existing conversation by appending a user turn instead of
   * resetting — this is what makes an interactive REPL feel like one conversation. The
   * leading system message is (re)synced to the current prompt, so a resumed session whose
   * tools were rebuilt gets a matching catalog rather than a stale one.
   */
  async send(task: MessageContent): Promise<RunResult> {
    await this.ensureFs();  // resolve the disk default (if no fs was passed) before prepare reads o.fs
    const systemPrompt = await this.prepare(contentText(task)); // first turn's task hints catalog relevance; frozen thereafter (memoized → cache-stable)
    const startCtx = await this.fireSessionStart();      // [lifecycle] first send() of a fresh agent counts as session start
    const userContent = await this.applyPromptSubmit(task);
    const sys = systemPrompt + (startCtx ? '\n\n' + startCtx : '');
    if (this.transcript[0]?.role === 'system') this.transcript[0] = { role: 'system', content: sys };
    else this.transcript.unshift({ role: 'system', content: sys });
    this.transcript.push({ role: 'user', content: userContent });
    return this.runLoop();
  }

  /**
   * Fold the conversation in place (manual `/compact`) until it fits a TOKEN budget — not a message
   * count. Message-count folding alone frees almost nothing when a few recent tool results are huge,
   * so we escalate: fold the middle into a synthetic recap → elide oversized tool-result bodies in the
   * retained window → halve the window → drop-oldest backstop. Deterministic, no LLM call.
   * `focus` (e.g. `/compact keep the API details`) preserves matching lines from the dropped span verbatim.
   * Returns before/after token estimates so the caller can report honestly (0 freed => say so).
   */
  compactNow(maxMessages = 12, focus?: string): CompactResult {
    const cap = Agent.contextCap(this.options);
    const target = Math.round(cap * Agent.COMPACT_TARGET);
    // Budget on the WIRE view (same estimator as /context + the footer) — see estimateTokens.
    const kr = this.options.keepRecentImages ?? 1;
    const tokensBefore = estimateTokens(this.transcript, kr);
    const msgsBefore = this.transcript.length;
    let max = Math.max(2, maxMessages);
    // Nothing to fold AND already under budget: a genuine no-op. Return BEFORE onPreCompact — hooks that
    // persist a summary must not write a spurious one for a `/compact` that changes nothing.
    if (this.transcript.length <= max && tokensBefore <= target)
      return { tokensBefore, tokensAfter: tokensBefore, target, cap, removed: 0 };
    void this.activeHooks?.onPreCompact?.(this.transcript); // [lifecycle] observe before folding (fire-and-forget)
    let out = this.transcript;
    if (out.length > max) out = compact(out, max, focus);
    // Inline base64 can't be shaved, only dropped — so drop the whales FIRST. Otherwise a single oversized
    // screenshot is paid for by shredding cheap text messages further down the ladder.
    // The NEWEST image is spared on this pass: `releaseStaleImages(keepRecent = 1)` deliberately keeps it,
    // and a user who pastes a screenshot and then runs `/compact` must still be able to ask about it.
    if (estimateTokens(out, kr) > target) out = elideOversizedImages(out, target, true, kr);
    if (estimateTokens(out, kr) > target) out = capOversizedBodies(out, target, kr);
    // Only once the text ladder has done all it can does the newest image become fair game.
    if (estimateTokens(out, kr) > target) out = elideOversizedImages(out, target, false, kr);
    // Still over (e.g. one giant user paste / assistant reply): halve the window, re-capping each pass.
    while (estimateTokens(out, kr) > target && max > 2 && out.length > 2) {
      max = Math.max(2, Math.floor(max / 2));
      out = capOversizedBodies(compact(out, max, focus), target, kr);
    }
    if (estimateTokens(out, kr) > target) out = fitTokenBudget(out, target, kr); // drop-oldest backstop
    out = dropOrphanToolResults(out); // never leave a tool_result whose tool_call was folded away
    this.transcript = out;
    this.releaseStaleImages(); // stale inline base64 is dead weight in the folded transcript too
    // Measure the transcript we actually kept (releaseStaleImages may have shrunk it further).
    return { tokensBefore, tokensAfter: estimateTokens(this.transcript, kr), target, cap, removed: msgsBefore - this.transcript.length };
  }

  /** Drop inline base64 images from older messages so Bun/JSC can reclaim the heap (see contentBytes). */
  releaseStaleImages(keepRecent = 1): void {
    const next = elideStaleImages(this.transcript, keepRecent);
    if (next !== this.transcript) this.transcript = next;
  }

  private async runLoop(): Promise<RunResult> {
    const o = this.options;
    const wireTools = toWireTools(this.activeTools);
    // Stream only when explicitly requested AND a host can render deltas; else stay on the non-stream path.
    const useStream = o.stream === true && typeof o.host?.notify === 'function';
    let steps = 0;
    const usage = { promptTokens: 0, completionTokens: 0, totalTokens: 0, cacheCreationTokens: 0, cacheReadTokens: 0 };
    let usageEstimated = false; // flips true the first turn a provider returns no usage and we estimate
    const start = Date.now();
    this.parkedMs = 0;       // reset per run: only THIS run's human-wait is excluded from the timeout
    this.ctx.needsInput = undefined; // reset per run: a question answered last run must not re-stop this one
    let toolCallsTotal = 0;  // cumulative tool calls dispatched
    let lastFp = '';         // fingerprint of the previous tool-call batch
    let repeats = 0;         // consecutive identical batches
    let emptyNudged = false; // an empty turn AFTER tool work gets ONE "you returned nothing" re-prompt before failing
    let closureNudged = false; // closeDelegatedTurns: the closure reflex fires at most once per run
    // Progress clock: `timeoutMs` bounds IDLE time (since last productive step), not total run length —
    // a single extended-thinking step can legitimately exceed the whole-run ceiling while actively
    // streaming reasoning (stallMs keeps that in-flight turn alive), so an absolute wall-clock would kill
    // completed, productive work. Runaway-but-active loops stay covered by maxSteps/maxToolCalls/maxRepeats/budget.
    let lastProgressAt = start;
    // Single exit for every kill-switch: warn + return a result carrying the reason.
    const kill = (finishReason: RunResult['finishReason']): RunResult => {
      log.warn(`kill-switch: ${finishReason} (steps=${steps}, tokens=${usage.totalTokens}, budgetTokens=${Math.round(usage.totalTokens - 0.9 * usage.cacheReadTokens)}, ms=${Date.now() - start - this.parkedMs}${finishReason === 'timeout' ? `, idle=${Date.now() - lastProgressAt - this.parkedMs}ms` : ''}${this.parkedMs ? ` +${this.parkedMs} parked` : ''})`);
      this.jobs.killAll(`run ended: ${finishReason}`, true); // abort turn-scoped jobs (don't commit) — a budget/timeout kill shouldn't leak writes
      const claims = this.survivingClaims();
      return { text: lastAssistantText(this.transcript), steps, finishReason, messages: this.transcript, usage, usageEstimated, ...(claims.length ? { fabricatedToolClaims: claims } : {}) };
    };

    const overBudget = () => !!o.maxTokens && usage.totalTokens - 0.9 * usage.cacheReadTokens >= o.maxTokens;
    // A corrective nudge (fabrication/closure) spends a step. If the next pre-step check would kill it,
    // don't nudge: a finished answer must end as 'stop', not be relabelled 'budget' by our own nudge.
    const canNudge = () => steps < o.maxSteps && !overBudget();

    while (true) {
      if (o.signal?.aborted) return kill('aborted');
      if (steps >= o.maxSteps) return kill('max_steps');
      if (o.timeoutMs && Date.now() - lastProgressAt - this.parkedMs >= o.timeoutMs) return kill('timeout'); // idle-since-last-progress: an active/long step resets the clock; parked prompt time excluded
      // Budget = cost proxy, not raw volume: cache reads are ~10% of fresh-token price, and a fat cached
      // context (MCP schemas + big tool results) re-counts every step — at full weight a healthy ~25k-ctx
      // session dies near step 8. Weighting reads at 0.1 keeps runaway protection (fresh tokens count full).
      if (overBudget()) return kill('budget');
      steps++;
      this.options.host?.notify?.({ kind: 'turn_start', message: `step ${steps}` });

      let res: ChatResponse;
      const sent = this.trimContext(); // the exact context sent this turn (reused for usage estimation)
      // Normalized reasoning → provider request shape; explicit providerOptions overrides the derived one.
      const frag = reasoningToChatFragment(o.model, o.reasoning);
      // claude-code/* is an autonomous runtime (Claude Agent SDK) that runs its OWN tools inside its
      // subprocess and rejects host-defined tool schemas — send none. External tools reach it via
      // providerOptions.mcpServers (wired by the CLI's claudeCodeProviderOptions). Mirror of cursor below.
      const sentTools = o.model.startsWith('claude-code/') ? [] : wireTools;
      // Cursor is an agent runtime — bridge host tools via IPC-based toolExecutor callback.
      const isCursorWithTools = o.model.startsWith('cursor/') && wireTools.length > 0;
      // Transport deadline with STRUCTURAL headroom over the slowest tool we actually registered. The
      // helper's watchdog used to be a bare 120_000 — identical to the Shell tool's own default cap — so
      // both fired at once, the transport won, and the shell tool's real `[exit 124] timed out ...`
      // result was replaced by an empty string the model could learn nothing from. Derived, never typed twice.
      //
      // FLOORED, because `maxDurationMs` is opt-in: a tool that does not declare one is NOT thereby prompt
      // (McpCall, Task and any host tool contribute 0), and deriving from declarers alone made declaring
      // Shell's 600s *lower* the deadline below the transport's own 30m default — an undeclared hung call
      // then had less headroom than before anyone declared anything. The floor is the transport default;
      // a declaration can only raise it.
      const hostToolTimeoutMs = (o.providerOptions as any)?.toolTimeoutMs;
      const derivedToolTimeoutMs = Math.max(TRANSPORT_TOOL_TIMEOUT_FLOOR_MS, ...this.activeTools.map(transportHeadroomMs)); // activeTools, not wireTools — the wire schema drops maxDurationMs
      // A host that sets providerOptions.toolTimeoutMs explicitly WINS — but only UPWARD. It cannot own
      // what it cannot see: `maxDurationMs` is assembled at run time from defaultTools() + preset shell +
      // addTools() + MCP discovery, so a host picking a number is picking it against a tool set it never
      // enumerates. Setting it BELOW the derived floor has exactly one consequence — the transport wins the
      // race and the tool's real result is replaced by '' — i.e. it re-creates, by configuration, the very
      // bug this fix removed, and does so silently-and-late (the warn scrolls past at step 1, the
      // destruction lands at step 6). So the escape hatch stays open in the only direction that helps:
      // raising the deadline. Clamped, not merely reported.
      const effectiveToolTimeoutMs = typeof hostToolTimeoutMs === 'number' ? Math.max(hostToolTimeoutMs, derivedToolTimeoutMs) : derivedToolTimeoutMs;
      this.toolHoldMs = effectiveToolTimeoutMs;
      this.nativeHoldMs = o.nativeHoldMs;
      if (isCursorWithTools) {
        if (typeof hostToolTimeoutMs === 'number' && effectiveToolTimeoutMs !== hostToolTimeoutMs) {
          log.warn(`providerOptions.toolTimeoutMs=${hostToolTimeoutMs}ms is below what the registered tools need — RAISED to ${effectiveToolTimeoutMs}ms.`
            + ` ${toolTimeoutViolations(this.activeTools, hostToolTimeoutMs).length} tool(s) could outlive it, and the transport would have destroyed their results.`
            + ` To lower it, lower the tools' own maxDurationMs (e.g. the Shell tool's maxTimeoutMs) instead.`);
        }
        // ASSERTED, not assumed: a tool that can outlive the transport deadline is exactly the collision
        // this whole fix removed. After the clamp this must be empty — keep it as the belt-and-braces check.
        for (const v of toolTimeoutViolations(this.activeTools, effectiveToolTimeoutMs)) log.warn(v);
      }
      const cursorPo = isCursorWithTools ? {
        toolTimeoutMs: effectiveToolTimeoutMs,
        toolExecutor: async (name: string, args: Record<string, any>) => {
          const tc = { id: `cursor-${Date.now()}`, type: 'function' as const, function: { name, arguments: JSON.stringify(args) } };
          // Return the FULL result, images included. The cursor adapter relays it to the helper,
          // which turns `{text, images}` into the SDK's tool-result content blocks. Flattening to
          // `.text` here (as this did) silently dropped every picture a tool produced — the model
          // got "here is the app as it is rendering right now" with no image, on cursor/* models
          // that are the DEFAULT for some hosts. The native loop below does the same via imagePart.
          // Hold the stall watchdog while the tool runs: the cursor stream is legitimately quiet until the
          // result goes back. Firing here (prod feedox 2026-09-16: a `nohup … &` Bash held its pipe 60s)
          // cancelled the run and RE-ISSUED the whole delegated loop from the pre-step transcript — the
          // retry never saw the tool that had already run, so it launched the same command again, and the
          // first call's real result landed on a torn-down run.
          const job = this.jobs.track({ kind: 'host', label: name, holds: true, hardCapMs: effectiveToolTimeoutMs });
          try { const r = await this.dispatch(tc); job.finish('done'); return r; }
          catch (e) { job.finish('error', undefined, String(e)); throw e; }
          finally { this.toolRanThisAttempt = true; this.stallPoke?.(); }
        },
      } : undefined;
      // claude-code warm sessions accept mid-turn steers via a `steer` pull source (the adapter drains it
      // into the SDK's InputQueue). Route text injections through it so they reach the live warm session;
      // non-text injections (and any that land after the last drain) fall through to the step-boundary re-open.
      const claudeSteerPo = o.model.startsWith('claude-code/') ? { steer: () => this.drainPendingSteers() } : undefined;
      const reasonOpts = {
        ...frag,
        ...(o.promptCache ? { promptCache: true } : {}),
        ...(o.providerOptions || cursorPo || claudeSteerPo ? { providerOptions: { ...frag.providerOptions, ...o.providerOptions, ...cursorPo, ...claudeSteerPo } } : {}),
      };
      try {
        // One step = one model call; a TRANSIENT network drop (mid-stream or pre-flight) retries the
        // step from the last committed transcript state instead of failing the whole turn. Server-side
        // transient HTTP errors (5xx/408/overloaded) retry too; 4xx like rate limits are NOT retried
        // here — the AIClient's own rate-limit retry handles those.
        for (let attempt = 0; ; attempt++) {
          for (const [, h] of this.nativeJobs) if (h.status === 'running') this.jobs.kill(h.id, 'step attempt ended'); // no longer awaited by any stream
          this.nativeJobs.clear();
          this.toolRanThisAttempt = false;
          try {
            if (useStream) {
              // Guard the whole request (time-to-first-chunk + inter-chunk gaps) with an idle-stall
              // watchdog: if the provider goes silent past o.stallMs, abort THIS request via a linked
              // controller so the loop can retry the step instead of hanging inside `chat()` forever.
              const stall = this.armStallWatchdog(o.signal, o.stallMs);
              try {
                const r = await o.ai.chat({ model: o.model, messages: sent, tools: sentTools, stream: true, signal: stall.signal, ...(o.toolChoice ? { toolChoice: o.toolChoice } : {}), ...reasonOpts });
                res = await this.consumeStream(r as AsyncIterable<StreamChunk>, stall.poke);
                // A watchdog-fired abort is a STALL even when the provider ends its stream CLEANLY on
                // abort instead of throwing (the cursor adapter releases the consumer with a normal end).
                // Without this check that clean end fell through to the empty-stop guard below and a
                // silent provider was retried as `code:'empty'` 5× and reported as finishReason:'empty'.
                if (stall.stalled()) throw stall.reclassify(undefined);
              } catch (err) {
                throw stall.reclassify(err); // a watchdog-fired abort → Error{code:'stall'}; parent/user aborts pass through
              } finally {
                stall.clear();
              }
            } else {
              const r = await o.ai.chat({ model: o.model, messages: sent, tools: sentTools, stream: false, signal: o.signal, ...(o.toolChoice ? { toolChoice: o.toolChoice } : {}), ...reasonOpts });
              res = r as ChatResponse;
            }
            // Empty-stop guard: a ZERO-WORK turn — the model went silent on the FIRST call, committing
            // nothing to the transcript, yet downstream would report it as a successful "done" (the real
            // agentx-composer "spins then ✓ done · 0 tools" incident). Scope to `steps === 1` (incremented
            // at the top of the loop, reset per-run): only the first model call can be zero-work, since any
            // later step follows tools/text already committed this turn — an empty wrap-up after real work
            // is a productive turn and must NOT be failed. Throw code:'empty' → the catch below retries it
            // through the SAME transient machinery (bounded by attempt<2); after the retries it propagates
            // to the outer catch which surfaces finishReason:'empty' (a failed turn), never a phantom stop.
            // Skip if a steer/injection is pending — that re-opens the turn naturally at the loop boundary.
            if (steps === 1 && this.isEmptyStop(res) && !this.pendingInjections.length) {
              const e = new Error('model returned an empty turn (finishReason=stop, no text, no tools)') as Error & { code: string };
              e.code = 'empty';
              throw e;
            }
            break;
          } catch (err) {
            const sc = (err as any)?.statusCode;
            const serverSide = sc >= 500 || sc === 408 || /Service Unavailable|overloaded|Internal server error|Bad gateway|Gateway time/i.test(String((err as any)?.message ?? ''));
            const network = !sc && /ECONNRESET|ECONNREFUSED|ETIMEDOUT|ENOTFOUND|EAI_AGAIN|EPIPE|socket hang up|fetch failed|network|terminated|UND_ERR/i.test(String((err as any)?.message ?? (err as any)?.code ?? err));
            const stalled = (err as any)?.code === 'stall'; // watchdog-fired: provider went silent mid-stream
            // Empty/silent turn: agentx's own zero-work guard (code:'empty') OR a delegated runtime signalling
            // a wedged content-less session at the source (cursor adapter's code:'cursor_silent'). Both retry
            // through the SAME machinery on the WIDER empty budget — the adapter reaps the wedged warm helper
            // so each attempt spawns a fresh session.
            const empty = (err as any)?.code === 'empty' || (err as any)?.code === 'cursor_silent';
            // Empty gets its own (wider) attempt cap + backoff; network/server/stall keep the fixed budget of 2.
            const maxAttempts = empty ? Math.max(o.emptyRetries, 0) : 2;
            // A stall after a tool already ran is NOT retried: the retry re-sends the pre-step transcript to a fresh
            // session, which never saw that tool and runs it again (duplicate side effects).
            const replaysTool = stalled && this.toolRanThisAttempt;
            if (replaysTool) log.warn('model stall after tool(s) already ran this step — not retrying (a retry would replay them)');
            const transient = !o.signal?.aborted && !isAbortError(err) && !replaysTool && attempt < maxAttempts && (network || serverSide || stalled || empty);
            if (!transient) throw err;
            const waitMs = (empty ? o.emptyRetryBaseMs : 1000) * (attempt + 1);
            log.warn(`${empty ? 'empty model turn' : stalled ? 'model stall' : 'network drop'} mid-step (${(err as any)?.message ?? err}) — retrying in ${waitMs}ms`);
            o.host?.notify?.({ kind: 'retry', message: `${empty ? 'model returned nothing' : stalled ? 'model stalled' : 'connection dropped'} — retrying step (#${attempt + 1})` });
            await new Promise((r) => setTimeout(r, waitMs));
          }
        }
      } catch (err) {
        // A budget-exhausted signal (e.g. proxy 429) is a clean stop, not a crash:
        // preserve the transcript so the run can be resumed once the cap is raised.
        if ((err as any)?.code === 'budget') return kill('budget');
        // Watchdog gave up after retries: the provider stayed silent. Clean stop (preserve transcript
        // for resume), labelled 'timeout' so it's diagnosable — not a crash, not a user 'aborted'.
        // `error` rides along (code:'stall') so a host can tell a provider stall from the between-steps idle ceiling.
        if ((err as any)?.code === 'stall') { log.warn(`model stalled out after retries: ${(err as any)?.message ?? err}`); return { ...kill('timeout'), error: err }; }
        // Abort wins over the empty/silent classification: an early barge-in of a cursor turn can surface as
        // a content-less end (code:'cursor_silent'), but a USER-aborted turn is 'aborted', never a failed
        // 'empty'. Check the signal FIRST. (The flag can LAG the throw under a barge-in race — the controller
        // fires, the provider rejects, but o.signal.aborted hasn't settled — so match the abort message too,
        // else a clean cancel is logged as a crash AND its "operation was aborted" string leaks onto the
        // spoken/utterance line.)
        if (o.signal?.aborted || isAbortError(err)) return kill('aborted');
        // Empty-stop after the bounded retries: the model kept returning nothing. Surface it as a failed
        // turn (finishReason:'empty' → the host prints ✗ / exits non-zero) instead of a phantom "done".
        if ((err as any)?.code === 'empty' || (err as any)?.code === 'cursor_silent') { log.warn(`model returned empty/silent turns after retries — surfacing as failed`); return { text: '', steps, finishReason: 'empty', messages: this.transcript, usage, usageEstimated, error: err }; }
        // surface the provider's human message (non-enumerable → lost by JSON.stringify); the bare
        // {code,statusCode} alone reads as a malformed request when it's often billing/quota (e.g. a 400
        // whose body is "credit balance is too low"). The raw response body carries that detail —
        // extract it onto the error so hosts/logs see WHY, not just the status code.
        const body = (err as any)?.body ?? (err as any)?.response?.data ?? (err as any)?.error;
        // try/catch: a circular body must not crash the error handler itself
        let bodyStr: string | undefined;
        try { bodyStr = body && typeof body !== 'string' ? JSON.stringify(body).slice(0, 2000) : (body as string | undefined); } catch { bodyStr = undefined; }
        if (bodyStr && err instanceof Error && !err.message.includes(bodyStr)) (err as any).detail = bodyStr;
        // Error properties are non-enumerable → JSON.stringify(err) = {} — serialize explicitly so logs are useful
        const errLog = err instanceof Error ? { message: err.message, ...((err as any).detail ? { detail: (err as any).detail } : {}), stack: err.stack } : err;
        log.error(`chat() failed: ${(err as any)?.message ?? err}${bodyStr ? ` — ${bodyStr}` : ''}`, errLog);
        return { text: '', steps, finishReason: 'error', messages: this.transcript, usage, usageEstimated, error: err };
      }
      // Cancelled mid-response → end the turn as 'aborted', not as a clean 'stop' on the partial reply.
      if (o.signal?.aborted) return kill('aborted');

      // ai.libx.js streaming doesn't surface token usage — estimate (~4 chars/token) so cost reporting
      // AND the maxTokens budget kill-switch still work when streaming (the CLI default). Non-stream
      // responses carry exact usage and are left untouched.
      if (!res.usage) {
        const promptTokens = estimateTokens(sent);
        const completionTokens = Math.ceil((contentText(res.content).length + (res.toolCalls ? JSON.stringify(res.toolCalls).length : 0)) / 4);
        res.usage = { promptTokens, completionTokens, totalTokens: promptTokens + completionTokens };
        usageEstimated = true;
      }
      if (res.usage) {
        usage.promptTokens += res.usage.promptTokens ?? 0; usage.completionTokens += res.usage.completionTokens ?? 0; usage.totalTokens += res.usage.totalTokens ?? 0;
        usage.cacheCreationTokens += (res.usage as any).cacheCreationTokens ?? 0; usage.cacheReadTokens += (res.usage as any).cacheReadTokens ?? 0;
      }
      // A delegated runtime (cursor/claude-code) ran its tools inside its own loop — they reach us only
      // as display-only toolActivity, never the transcript, leaving it narration-only (non-auditable and
      // blind on resume). Reconstruct assistant(tool_calls)+tool pairs so the transcript matches a native
      // turn's shape. Output trimmed via the SAME cap+cropResult path native results use. Pushed BEFORE
      // the final text (the runtime's summary came after its tools).
      const delegatedTools = res.delegatedTools ?? [];
      if (delegatedTools.length) {
        const cap = this.options.maxToolResultBytes ?? 0;
        this.transcript.push({
          role: 'assistant',
          content: '',
          tool_calls: delegatedTools.map((a) => ({ id: a.id, type: 'function' as const, function: { name: a.name || 'tool', arguments: (() => { try { return JSON.stringify(a.input ?? {}); } catch { return '{}'; } })() } })),
        });
        for (const a of delegatedTools) {
          const name = a.name || 'tool';
          // A delegated HOST tool returns dispatch()'s own shape — `{text, images}` — because the cursor
          // toolExecutor relays that verbatim. JSON-stringifying it turned every picture into a wall of
          // base64 text; rebuild image parts here exactly as the native loop does below.
          const r = delegatedResult(a.output);
          // The floor's invariant is MEANINGFUL content, not merely a non-empty string: `{}`, `[]` and an
          // empty MCP `{content:[]}` stringify to something non-empty that teaches the model exactly as
          // little as ''. Scalars a tool meant to return (a real 0, a real false) are meaningful and pass.
          // Images alone are a real result, so only floor the text when nothing else came.
          const text = r.images.length ? r.text : nonEmptyResult(isVacuous(a.output) ? '' : r.text, name, deliveryOf(a.output, a.status));
          const body = cap > 0 && text.length > cap ? cropResult(text, cap) : text;
          const content: MessageContent = r.images.length
            ? [{ type: 'text', text: body } as ContentPart, ...r.images.map((i) => imagePart(`data:${i.mimeType};base64,${i.data}`))]
            : body;
          this.transcript.push({ role: 'tool', tool_call_id: a.id, content });
        }
      }

      const toolCalls = res.toolCalls ?? [];
      // A turn with no tool calls AND no text is dead air — don't commit it to the transcript or count
      // it as a real response (it bills tokens but says/does nothing). The voice layer repairs the silence.
      // Delegated tools above are real work, so a turn that ran them is never "empty".
      const emptyTurn = toolCalls.length === 0 && delegatedTools.length === 0 && contentText(res.content ?? '').trim() === '';
      if (!emptyTurn) lastProgressAt = Date.now(); // productive step (text/tool/delegated) → reset the idle timeout window
      if (!emptyTurn) {
        // Reasoning is kept on the stored message ONLY for providers whose adapter passes it back
        // (DeepSeek 400s on a tool loop without it; Kimi loses its chain of thought). Tradeoff: it rides
        // along in the session file and counts toward context bytes — but it is a separate field, so
        // `content`-based rendering, summaries and compaction text never show it.
        this.transcript.push({
          role: 'assistant',
          content: res.content ?? '',
          ...(toolCalls.length ? { tool_calls: toolCalls } : {}),
          ...(res.reasoningContent && passesBackReasoning(o.model) ? { reasoning_content: res.reasoningContent } : {}),
        });
      }

      if (toolCalls.length === 0) {
        // A message injected while the model was composing its final reply re-opens the turn (CC
        // parity) instead of being lost to the post-turn queue — the model answers it in-place.
        if (this.drainInjections()) continue;
        // Empty turn that slipped past the step-1 retry guard — typically AFTER work: the model read/ran
        // tools then returned nothing (seen on anthropic/claude-opus-5). Reporting that as 'stop' made headless
        // workers print ✓ done / ok:true with no output. Re-prompt ONCE; still empty → a failed turn.
        if (emptyTurn && o.failEmptyTurns) {
          if (!emptyNudged && canNudge()) {
            emptyNudged = true;
            log.warn(`empty model turn after tool work (step ${steps}, provider finishReason=${res.finishReason ?? 'n/a'}) — nudging once`);
            this.transcript.push({ role: 'user', content: 'Continue — you returned nothing. Answer the user now using the tool results above, or keep working if you are not done.' });
            continue;
          }
          log.warn(`model returned empty turns after tool work — surfacing as failed`);
          const e = new Error('empty_response: model returned an empty turn (no text, no tools) after a re-prompt') as Error & { code: string };
          e.code = 'empty';
          return { text: '', steps, finishReason: 'empty', ...(res.finishReason ? { providerFinishReason: res.finishReason } : {}), messages: this.transcript, usage, usageEstimated, error: e };
        }
        // Closure reflex: a delegated runtime ended on a tool with no wrap-up. Nudge ONE closing step.
        // Ask for the ANSWER, not a process report: the tool records are already in the transcript, so a
        // "summarize what you did" phrasing made the model narrate its own turn ("I ran 4 searches, they
        // were interrupted, want me to retry?") instead of answering the user — and invited it to re-run
        // work it had already completed. Name those failure modes so it can't slide back into them. Do NOT
        // assert the results are complete (a tool may have errored) — that would push it to fabricate;
        // instead give it an explicit out to report the failure.
        // FABRICATION reflex. Sits BEFORE the closure reflex and shares its one-shot budget: both are the
        // same disease (a turn that reports work it did not do), and firing two nudges in a row would just
        // spend two metered steps. This one is checked first because it is the PROVABLE case — it names the
        // tool and can demand the actual call, where the closure nudge can only ask for an answer.
        // Reachability: this branch is inside `toolCalls.length === 0`, which is exactly the shape a
        // fabricated turn has — the guard is evaluated on the path the defect actually takes, not on a
        // sibling branch that a fabricating turn can never enter.
        // NOT gated on `delegatedTools.length === 0`. It was, and that made the guard DEAD on every
        // delegated backend: on `cursor/*` one chat() call IS the whole agent session, so its own tool
        // calls arrive as `toolActivity` and its final text arrives in the same response — meaning any
        // turn that did real work has `delegatedTools.length > 0` and the check never ran. That is
        // precisely the turn the defect was reported on (edit the app, then invent a `look_at_app`
        // failure). The conjunct was also unnecessary: delegated activity is recorded into
        // `invokedTools` as it streams, so a tool named in the text but absent from the ledger is
        // provably uncalled no matter who executed the rest of the turn.
        const fabricated = o.catchFabricatedToolUse && !closureNudged
          ? this.fabricatedToolNames(contentText(res.content ?? '')) : [];
        for (const n of fabricated) this.fabricatedClaims.add(n);
        if (fabricated.length && !canNudge()) log.warn(`fabricated tool use: text names ${fabricated.join(', ')} but no step/budget left to demand the call — finishing with the existing answer`);
        if (fabricated.length && canNudge()) {
          closureNudged = true;
          log.warn(`fabricated tool use: text names ${fabricated.join(', ')} but the run invoked ${this.invokedTools.size ? [...this.invokedTools].join(', ') : 'nothing'} — demanding the real call`);
          this.transcript.push({
            role: 'user',
            content: `You referred to ${fabricated.map((n) => `\`${n}\``).join(', ')} in your reply, but you never actually called `
              + `${fabricated.length > 1 ? 'those tools' : 'that tool'} — no tool call was made, so any result you reported for `
              + `${fabricated.length > 1 ? 'them' : 'it'} was invented. Either call `
              + `${fabricated.length > 1 ? 'them' : 'it'} for real now, or answer without ${fabricated.length > 1 ? 'them' : 'it'} and do `
              + `NOT claim any outcome — including a failure or a timeout — for a call you did not make.`,
          });
          continue;
        }
        if (o.closeDelegatedTurns && res.endedWithoutClosing && !closureNudged && canNudge()) {
          closureNudged = true;
          log.verbose('delegated turn went silent after a tool — nudging a closing answer');
          this.transcript.push({
            role: 'user',
            content: 'You ran tools but did not reply. Answer the user now using the tool results above; '
              + 'if a tool failed, say so plainly. Do NOT re-run those tools, do NOT narrate what you did, '
              + 'and do NOT ask for permission to continue.',
          });
          continue;
        }
        log.verbose(`completed in ${steps} step(s)`);
        const drained = await this.jobs.drain(o.signal); // let background jobs finish + commit before we call the run done (no lost writes)
        if (o.signal?.aborted) return kill('aborted'); // a user cancel stops turn-scoped jobs too (kill → killAll drainable)
        // Waiting on a job that finished is progress: without this a job longer than the idle `timeoutMs` re-opens the turn only to be killed as 'timeout', its result unseen.
        if (drained.length) lastProgressAt = Date.now();
        // A handed-off job (e.g. a slow MCP call) that completed during the drain re-opens the turn with its result.
        if (this.drainInjections()) continue;
        this.releaseStaleImages(); // inline screenshots are one-shot context — don't immortalize MB of base64
        await this.activeHooks?.onStop?.(res.content ?? ''); // [hooks] fire once on clean stop
        const claims = this.survivingClaims();
        return { text: res.content ?? '', steps, finishReason: 'stop', ...(res.finishReason ? { providerFinishReason: res.finishReason } : {}), messages: this.transcript, usage, usageEstimated, ...(claims.length ? { fabricatedToolClaims: claims } : {}) };
      }

      // loop detection: the model emitting the same tool-call batch over and over.
      const fp = toolCalls.map((tc) => tc.function.name + ':' + (tc.function.arguments ?? '')).join('|');
      repeats = fp === lastFp ? repeats + 1 : 1;
      lastFp = fp;
      if (o.maxRepeats && repeats >= o.maxRepeats) return kill('loop');

      toolCallsTotal += toolCalls.length;
      if (o.maxToolCalls && toolCallsTotal > o.maxToolCalls) return kill('max_tool_calls');

      for (const tc of toolCalls) {
        if (o.signal?.aborted) return kill('aborted'); // cancelled mid-batch → don't run the rest
        const raw = await this.dispatch(tc);
        let content: MessageContent;
        if (typeof raw === 'string') { content = nonEmptyResult(raw, tc.function.name); }
        else {
          // Images alone are a real result; text alone is not, so only floor the text when nothing else came.
          const hasImages = !!raw.images?.length;
          const parts: ContentPart[] = [{ type: 'text', text: hasImages ? raw.text : nonEmptyResult(raw.text ?? '', tc.function.name) }];
          for (const img of raw.images ?? []) parts.push(imagePart(`data:${img.mimeType};base64,${img.data}`));
          content = parts;
        }
        this.transcript.push({ role: 'tool', tool_call_id: tc.id, name: tc.function.name, content });
      }
      // A tool that ran is progress: a long (legitimately bounded) tool must not trip the idle `timeoutMs` at the next step boundary.
      lastProgressAt = Date.now();
      // AskUserQuestion with no reachable human: stop and hand the question to the CALLER rather than
      // let the model pick for them. Runs AFTER the batch so the tool result is in the transcript —
      // the session persists cleanly and a resume continues from the answer. A clean pause, not a
      // kill: background jobs drain + commit (unlike kill(), which discards them).
      if (this.ctx.needsInput) {
        const question = this.ctx.needsInput;
        this.ctx.needsInput = undefined;
        log.warn(`needs_input: ${question.question} (steps=${steps})`);
        await this.jobs.drain();
        const text = [
          `OPEN QUESTION (run paused — no human was reachable): ${question.question}`,
          ...question.options.map((o) => `  • ${o.label}${o.description ? ` — ${o.description}` : ''}`),
        ].join('\n');
        return { text, steps, finishReason: 'needs_input', messages: this.transcript, usage, usageEstimated, question };
      }
      // Mid-turn steering: fold queued user input in after the tool results so the next model step
      // sees it alongside them (Claude-Code parity). No-op unless the host called inject() this step.
      this.drainInjections();
    }
  }

  /**
   * Drain a streamed chat() response: emit each text delta to the host
   * (`{kind:'text_delta'}`) and fold all chunks back into the single
   * ChatResponse the non-stream path would have returned.
   */
  /**
   * Idle-stall watchdog for one streamed model request. Returns a linked AbortController `signal` to
   * hand to `chat()`, a `poke()` to reset the idle timer on each chunk, a `reclassify()` that turns a
   * watchdog-fired abort into an Error{code:'stall'} (leaving a parent/user abort untouched), and a
   * `clear()` to disarm. Aborts the request if no `poke()` (or completion) happens within `stallMs`.
   * `stallMs = 0` disables the timer but still links parent-abort → child so cancellation propagates.
   */
  private armStallWatchdog(parent: AbortSignal | undefined, stallMs: number) {
    const ac = new AbortController();
    let fired = false;
    const onParentAbort = () => ac.abort();
    if (parent?.aborted) ac.abort();
    else parent?.addEventListener('abort', onParentAbort, { once: true });
    let timer: ReturnType<typeof setTimeout> | undefined;
    const stallError = () => {
      const e = new Error(`model stalled: no stream output for ${stallMs >= 1000 ? `${Math.round(stallMs / 1000)}s` : `${stallMs}ms`}`) as Error & { code: string };
      e.code = 'stall';
      return e;
    };
    const poke = () => {
      if (!stallMs) return;
      if (timer) clearTimeout(timer);
      // Abort WITH a coded reason: the provider sees `signal.reason.code === 'stall'` and can tell a
      // watchdog cancel (reap the silent session, retry fresh) from a user barge-in (keep it warm).
      // A host tool in flight is not a provider stall — re-arm instead of aborting (the tool is bounded by
      // its own deadline + the transport watchdog, and its completion pokes a fresh window).
      timer = setTimeout(() => {
        // A hold SUPPRESSES stall detection, so it must never be silent: the 2026-09-18 blank run spent
        // 61 minutes printing nothing at all because a wedged native tool re-armed this timer unseen.
        // Say who is holding and for how long, every window — one line a minute IS the diagnosis.
        if (this.toolHoldActive()) { // reaps first — so this reports who is STILL holding, post-reap
          const now = Date.now();
          const who = this.jobs.holding().map((j) => `${j.kind} "${j.label}" (${Math.round((now - j.startedAt) / 1000)}s, quiet ${Math.round((now - j.lastActivityAt) / 1000)}s)`).join(', ');
          log.warn(`stall watchdog: no stream output for ${stallMs}ms — held by ${who}`);
          this.options.host?.notify?.({ kind: 'tool_hold', message: `waiting on ${who}` });
          return poke();
        }
        const inFlight = this.jobs.list().filter((j) => j.status === 'running');
        log.warn(`stall watchdog: no stream output for ${stallMs}ms and no tool holding the stream — aborting the request`
          + (inFlight.length ? ` (${inFlight.length} detached job(s) running, not awaited by the stream: ${inFlight.map((j) => `${j.id}/${j.kind}`).join(', ')})` : ''));
        fired = true; ac.abort(stallError());
      }, stallMs);
    };
    this.stallPoke = poke;
    poke(); // arm before the request so time-to-first-chunk is guarded too
    // Distinguish OUR abort (silent provider) from a parent/user cancel, which must stay an abort.
    const stalled = () => fired && !parent?.aborted;
    return {
      signal: ac.signal,
      poke,
      stalled,
      clear: () => { if (timer) clearTimeout(timer); if (this.stallPoke === poke) this.stallPoke = undefined; parent?.removeEventListener('abort', onParentAbort); },
      reclassify: (err: unknown) => (stalled() ? stallError() : err),
    };
  }

  /** A host or native delegated tool is running and not stuck — the provider is quiet, not stalled. The reap runs
   *  FIRST, so a holder past its deadline is released (logged with evidence) instead of holding forever. */
  private toolHoldActive(): boolean {
    this.jobs.reap();
    return this.jobs.holding().length > 0;
  }

  /** A clean-stop turn that carries no assistant text AND no tool calls (native or delegated) — a
   *  non-result the model produced by going silent. Callers retry it rather than report "done". */
  private isEmptyStop(res: ChatResponse): boolean {
    const stop = res.finishReason === 'stop' || res.finishReason == null;
    return stop && (res.toolCalls?.length ?? 0) === 0 && (res.delegatedTools?.length ?? 0) === 0 && contentText(res.content ?? '').trim() === '';
  }

  private async consumeStream(stream: AsyncIterable<StreamChunk>, poke?: () => void): Promise<ChatResponse> {
    let content = '';
    let finishReason: string | undefined;
    let toolCalls: ToolCall[] | undefined;
    let usage: ChatResponse['usage'];
    // Delegated-runtime tools (cursor/claude-code loop internally) surface ONLY as display-only
    // toolActivity — accumulate + correlate by id (cursor: one `completed` event carrying everything;
    // claude-code: a `running` half with name+input, then a `completed`/`error` half with output) so
    // the loop can persist them into the transcript. First-seen order is preserved.
    const delegated = new Map<string, NonNullable<ChatResponse['delegatedTools']>[number]>();
    const delegatedOrder: string[] = [];
    // Closure signal: did the runtime CLOSE this turn? Track whether any tool ran and what assistant text
    // followed the LAST one. Trailing text alone isn't closure — cursor narrates as it works and its run
    // can settle while the narration is still mid-plan ("Also checking for X."), so the turn looks closed
    // but answers nothing. Decided after the stream drains, from `sawTool` + `trailing`.
    let sawTool = false;
    let trailing = '';
    let reasoning = '';
    for await (const chunk of stream) {
      poke?.(); // progress arrived → reset the idle-stall watchdog
      if (this.options.signal?.aborted) break; // cancelled → stop emitting now; breaking also closes the stream
      // [G3] surface extended-thinking as a distinct channel (never folded into `content`). It is also
      // accumulated so the loop can keep it as `reasoning_content` for providers that need passback.
      if (chunk.reasoningContent) { reasoning += chunk.reasoningContent; this.options.host!.notify!({ kind: 'thinking_delta', message: chunk.reasoningContent }); }
      // Display-only tool activity from a self-executing backend (Cursor CLI runs its OWN tools) — surface
      // for host tool cards; never dispatched. `message` gives plain hosts a printable line.
      if (chunk.toolActivity) {
        const a = chunk.toolActivity;
        const verb = a.status === 'error' ? '✗' : a.status === 'completed' ? '✓' : '→';
        this.options.host!.notify!({ kind: 'tool_activity', id: a.id, name: a.name, input: a.input, output: a.output, status: a.status, truncated: a.truncated, message: `${verb} ${a.name}` });
        const key = a.id ?? `${a.name}:${delegatedOrder.length}`;
        const prev = delegated.get(key);
        if (!prev) { delegated.set(key, { id: key, name: a.name, input: a.input, output: a.output, status: a.status }); delegatedOrder.push(key); }
        else { // merge halves: the completed/error event carries output; the running one carries name/input
          if (a.name) prev.name = a.name;
          if (a.input !== undefined) prev.input = a.input;
          if (a.output !== undefined) prev.output = a.output;
          prev.status = a.status;
        }
        const nj = a.id ? this.nativeJobs.get(a.id) : undefined;
        if (a.status === 'running') {
          if (nj) nj.touch();
          // `idleMs` (not just the transport hard cap) is what makes this hold evidence-based: a native
          // tool that keeps emitting `running` touches the job and holds for as long as it keeps proving
          // liveness, while pure silence expires in minutes instead of the ≥30m transport deadline.
          else if (a.id) this.nativeJobs.set(a.id, this.jobs.track({ kind: 'native', label: a.name, holds: true, idleMs: this.nativeHoldMs, hardCapMs: this.toolHoldMs }));
        } else {
          if (nj) { if (typeof a.output === 'string') nj.chunk(a.output.slice(-4000)); nj.finish(a.status === 'error' ? 'error' : 'done'); this.nativeJobs.delete(a.id!); }
          this.toolRanThisAttempt = true;
        }
        sawTool = true;
        this.invokedTools.add(a.name); // ledger: a delegated runtime's own tools are real calls too
        trailing = ''; // a tool just ran — only text AFTER it can close the turn
      }
      if (chunk.content) {
        content += chunk.content;
        trailing += chunk.content;
        this.options.host!.notify!({ kind: 'text_delta', message: chunk.content });
      }
      if (chunk.finishReason) finishReason = chunk.finishReason;
      if (chunk.toolCalls?.length) toolCalls = chunk.toolCalls; // accumulated, latest wins
      if (chunk.usage) usage = chunk.usage; // exact usage on the terminal chunk (providers that report it; else runLoop estimates)
    }
    const delegatedTools = delegatedOrder.map((k) => delegated.get(k)!);
    // Unclosed = tools ran and the turn either went silent after them or trailed off mid-plan.
    // Stays gated on `sawTool` ON PURPOSE. Ungating it to reach the announce-and-stop turn (which runs no
    // tools, so this flag can never see it) was tried and reverted: `endsOnAnnouncedAction` is prose
    // matching, and once it no longer needs a tool to have run it fires on ordinary conversation —
    // "I will look at the app once you deploy it." is a sentence about the USER's next step, and nudging it
    // spends a metered step talking over a finished answer. The announce-and-stop case is caught instead by
    // the invocation ledger (see fabricatedToolNames), which is ground truth rather than phrasing.
    const endedWithoutClosing = sawTool && (!trailing.trim() || endsOnAnnouncedAction(trailing));
    return { content, ...(reasoning ? { reasoningContent: reasoning } : {}), ...(finishReason ? { finishReason } : {}), ...(toolCalls ? { toolCalls } : {}), ...(usage ? { usage } : {}), ...(delegatedTools.length ? { delegatedTools } : {}), ...(endedWithoutClosing ? { endedWithoutClosing: true } : {}) };
  }


  /**
   * FABRICATED TOOL USE — the failure no call-reactive layer can see, because there IS no call.
   *
   * The model announces a tool ("I'll take a look with look_at_app"), emits no tool_use block at all, and
   * then writes a plausible RESULT for the call it never made ("the app view didn't respond in time"). To
   * every surface that turn is indistinguishable from a genuine timeout: the user reads a credible failure
   * sentence, the logs show no tool call because none was made, and nothing errors. Malformed-call handling
   * (dispatch's earlyError) cannot reach it — that path needs a tool_use block to exist.
   *
   * The discriminator is NOT prose. It is the per-run invocation ledger, which is ground truth: an advertised
   * tool NAMED in the model's own text while `invokedTools` holds zero calls to it is a provable claim about
   * an event that did not happen, not a guess about phrasing. Matching is on the exact registered tool name
   * at word boundaries, so it cannot fire on ordinary English the way `endsOnAnnouncedAction` can.
   *
   * Deliberately narrow, because a false positive costs a metered step:
   *  - only tools with a distinctive name (>= 4 chars, containing `_`, `-` or a capital) — a tool named `run`
   *    or `web` would match half of English prose;
   *  - only against the ledger for the WHOLE run, so referring back to a call it genuinely made earlier
   *    ("the screenshot showed an empty grid") is honest and stays silent;
   *  - only on a turn that made no tool calls of its own — a turn that DID call tools is closing normally.
   *    (That is the enclosing `toolCalls.length === 0` branch. It is NOT extended to delegated activity:
   *    a `cursor/*` turn runs its own tools and then fabricates in the SAME response, which is the exact
   *    reported defect — see the call site.)
   *
   * What it deliberately does NOT catch, both verified:
   *  - a fabrication that names no tool at all ("Let me take a look at the running app. The app view did
   *    not respond in time.") — there is nothing to prove it against. Only prose matching could reach it,
   *    and ungating `endsOnAnnouncedAction` for that was tried and reverted (it fires on ordinary English).
   *  - a SECOND invented result for a tool the run genuinely called earlier. The ledger is per-run by
   *    design, because that is the same fact that keeps an honest back-reference ("the screenshot showed
   *    an empty grid") silent. Distinguishing them would need per-claim positional accounting, which buys
   *    a rarer case at the cost of the common honest one.
   */
  private fabricatedToolNames(text: string): string[] {
    const out: string[] = [];
    // Delegated runtimes report their own names ("read", "shell"); compare on the canonical key.
    const invoked = new Set([...this.invokedTools].map(toolKey));
    for (const t of this.activeTools) {
      if (invoked.has(toolKey(t.name))) continue;
      if (t.name.length < 4 || !/[_\-A-Z]/.test(t.name)) continue; // too generic to match on safely
      const re = new RegExp(`(?:^|[^\\w])${t.name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(?:[^\\w]|$)`);
      if (re.test(text)) out.push(t.name);
    }
    return out;
  }

  /** The claims still standing at return time: named in this run's prose, and STILL absent from the
   *  invocation ledger. Re-checked here rather than trusted from detection because the ledger moves —
   *  the recovery nudge can succeed, and then the host would tell the user "I never ran it" about a
   *  call that demonstrably ran. See `RunResult.fabricatedToolClaims`. */
  private survivingClaims(): string[] {
    if (!this.fabricatedClaims.size) return [];
    const invoked = new Set([...this.invokedTools].map(toolKey));
    return [...this.fabricatedClaims].filter((n) => !invoked.has(toolKey(n)));
  }

  private async dispatch(tc: ToolCall): Promise<string | { text: string; images?: { mimeType: string; data: string }[] }> {
    this.invokedTools.add(tc.function.name); // ledger: recorded at DISPATCH, so even a failing call counts as made
    const tool = this.activeTools.find((t) => t.name === tc.function.name);
    // [render] Parse args first but DON'T early-return on failure. An unknown-tool or bad-args call
    // (e.g. a model hallucinating a `task` tool) must still flow through the same preToolUse/notify/
    // postToolUse path as a real call — otherwise the host draws no `⚙` line and the UI looks silently
    // stuck. On parse failure keep the raw string as `args` so it still renders, and fail below.
    let args: any = {};
    let earlyError: string | undefined;
    if (!tool) earlyError = `Error: unknown tool '${tc.function.name}'`;
    try {
      args = tc.function.arguments ? JSON.parse(tc.function.arguments) : {};
    } catch (e) {
      args = tc.function.arguments;
      earlyError ??= `Error: invalid JSON arguments for ${tc.function.name}: ${String(e)}`;
    }
    // Enforce the schema's `required` keys. Unenforced, a model using its own name for a param
    // (composer's `contents` for Write's `content`) ran the tool on `undefined` — Write then wrote a
    // 0-byte file and reported success (2026-09-24). Erroring lets the model self-correct.
    if (!earlyError && tool && args && typeof args === 'object') {
      const missing = ((tool.parameters as any)?.required ?? []).filter((k: string) => (args as any)[k] == null);
      if (missing.length) earlyError = `Error: ${tc.function.name} is missing required argument(s): ${missing.join(', ')} (got: ${Object.keys(args).join(', ') || 'none'}). Re-call with the exact parameter names.`;
    }
    const hooks = this.activeHooks; // [hooks] composed: user + plan-mode + permissions
    const call = { name: tc.function.name, args };
    const meta = { id: tc.id }; // [G4] stable per-call id so a host can correlate result↔call without serial-order assumptions
    // preToolUse is where a permission policy blocks on the user's Yes/No/Always answer — park that wait
    // so a long-standing approval prompt doesn't accrue toward the timeout kill-switch.
    const decision = await this.park(Promise.resolve(hooks?.preToolUse?.(call, meta))); // [hooks] guard before run
    if (decision?.block) {
      const blocked = `Blocked by hook: ${decision.reason ?? 'no reason given'}`;
      log.debug(`${tc.function.name} -> ${blocked}`);
      await hooks?.postToolUse?.(call, blocked, meta); // [hooks] observe the block too
      return blocked;
    }
    this.options.host?.notify?.({ kind: 'tool_use', id: tc.id ?? '', name: tc.function.name, input: args });
    // [render] surface the unknown-tool / bad-args failure through the normal result path (postToolUse
    // renders the body; notify carries isError) so it's visible instead of vanishing.
    if (earlyError) {
      log.debug(`${tc.function.name} -> ${earlyError}`);
      await hooks?.postToolUse?.(call, earlyError, meta);
      this.options.host?.notify?.({ kind: 'tool_result', id: tc.id ?? '', output: earlyError, isError: true });
      return earlyError;
    }
    let result: string;
    let images: { mimeType: string; data: string }[] | undefined;
    let threw = false;
    try {
      log.debug(`${tc.function.name}(${tc.function.arguments})`);
      // [hooks] per-call incremental-output channel: streaming tools (real Shell) push chunks via
      // ctx.emit → onToolOutput. Dispatch is serial, so binding on the shared ctx is safe; cleared
      // in finally so a straggler emit after the call settles is a silent no-op.
      this.ctx.emit = hooks?.onToolOutput ? (chunk: string) => { try { hooks.onToolOutput!(call, chunk, meta); } catch (e) { log.debug(`onToolOutput hook error: ${e}`); } } : undefined;
      const raw = await tool!.run(args, this.ctx);
      if (typeof raw === 'string') { result = raw; }
      else { result = raw.text; images = raw.images; }
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      log.debug(`${tc.function.name} -> error: ${msg}`);
      result = `Error: ${msg}`;
      threw = true;
    } finally {
      this.ctx.emit = undefined; // the call settled — late emits (stray timers) go nowhere
    }
    // autoTest (opt-in): after a successful write-class tool, run a VFS test command and surface any failure
    // to the model in-band — so it never burns a turn re-running tests itself. Skipped on block/throw.
    if (!threw) result = await this.maybeAutoTest(tc.function.name, result);
    // Image-only result (e.g. a screenshot tool): give it a text marker so hosts don't render
    // "(no output)" and the model has a text anchor next to the image part.
    if (images?.length && !result) result = `[${images.length} image${images.length > 1 ? 's' : ''} attached]`;
    // Pagination ceiling: a single oversized result (a 1MB Grep, a huge Read/MCP payload) would otherwise
    // blow the context window. Crop to page 1 with a marker; a capToolResult hook can spill it losslessly.
    // Browser tools (huge DOM/screenshot payloads) get a TIGHTER per-result cap once browserTrim is set —
    // this is the dominant per-step-growth lever for a long browse (a 15k-token DOM → small stub/spill).
    // Flip browserActive here so the NEXT trimContext can also keep fewer browser bodies verbatim.
    const browser = isBrowserTool(tc.function.name);
    if (browser && this.options.browserTrim) this.browserActive = true;
    const browserBytes = browser ? this.options.browserTrim?.resultBytes : undefined;
    const baseCap = this.options.maxToolResultBytes ?? 0;
    // tighter of (user cap, browser cap); a 0 (off) on either side means "no extra limit from that side".
    const cap = browserBytes ? (baseCap > 0 ? Math.min(baseCap, browserBytes) : browserBytes) : baseCap;
    if (!threw && cap > 0 && result.length > cap) {
      const info = { tool: tc.function.name, args };
      result = this.options.capToolResult ? await this.options.capToolResult(result, info) : cropResult(result, cap);
    }
    await hooks?.postToolUse?.(call, result, meta); // [hooks] observe the result
    this.options.host?.notify?.({ kind: 'tool_result', id: tc.id ?? '', output: result, isError: threw });
    if (images?.length) {
      for (const img of images) {
        this.options.host?.notify?.({ kind: 'tool_result_image', id: tc.id ?? '', dataUrl: `data:${img.mimeType};base64,${img.data}` });
      }
    }
    return images?.length ? { text: result, images } : result;
  }

  /** `/compact` aims to land the transcript at this fraction of the context window. */
  private static readonly COMPACT_TARGET = 0.5;

  /** THE context-window cap resolution — `/compact`'s budget and the CLI's context footer/warnings both
   *  route here, so the percentage the user reads and the one compaction targets can never drift. */
  static contextCap(o: Pick<AgentOptions, 'maxContextTokens' | 'contextWindow' | 'model'> | Partial<AgentOptions>): number {
    if (o.maxContextTokens) return o.maxContextTokens;
    const w = o.contextWindow || 0;
    // Anthropic serves >200k ONLY when the request carries the `context-1m-2025-08-07` beta header, which
    // we never send (the adapter emits files-api + interleaved-thinking only). A declared 1M window is
    // therefore unusable on the wire — budgeting against it would make /compact refuse to act at 300k.
    // Lifting this is a one-liner once that beta header is emitted.
    if (w > ANTHROPIC_WINDOW_WITHOUT_1M_BETA && isAnthropicModel(o.model)) return ANTHROPIC_WINDOW_WITHOUT_1M_BETA;
    return w || 200_000;
  }

  private static readonly WRITE_CLASS = ['Write', 'Edit', 'MultiEdit', 'ApplyEdits'];

  /** Append an autoTest failure section to a write-class tool result, if configured. */
  private async maybeAutoTest(toolName: string, result: string): Promise<string> {
    const at = this.options.autoTest;
    if (!at?.command) return result;
    const set = at.tools ?? Agent.WRITE_CLASS;
    if (!set.includes(toolName)) return result;
    const r = await this.ctx.exec.execute(at.command);
    if (r.exitCode === 0) return result; // silent on pass — avoid noise
    const out = truncateOutput(((r.output ?? '') + (r.error ?? '')).replace(/\n+$/, ''));
    return `${result}\n\n[autoTest] \`${at.command}\` FAILED (exit ${r.exitCode}):\n${out}`;
  }

  /**
   * Shape the per-turn context (a VIEW — the stored transcript is never mutated). Layered, each off by default:
   *  1. coarse reduction — `compaction` (fold the dropped middle into a synthetic summary) OR
   *     `maxContextMessages` (pure drop-oldest), keeping the system message + most-recent window.
   *  2. note-taking (`keepToolOutputs`) — collapse all-but-the-recent-N tool-result bodies to one-line stubs
   *     (kills "immortal tool results": a big Read/Grep stops costing its full weight once it's old).
   *  3. token-aware backstop (`maxContextTokens`) — drop oldest messages until under an estimated token ceiling.
   * With every knob off, returns the transcript unchanged (same reference).
   */
  trimContext(): Message[] {
    const o = this.options;
    const m = this.transcript;
    let out: Message[] | null = null;
    if (o.compaction?.maxMessages && m.length > o.compaction.maxMessages) out = compact(m, o.compaction.maxMessages);
    else if (o.maxContextMessages && m.length > o.maxContextMessages) out = dropOldest(m, o.maxContextMessages);
    // Once a browser tool has actually run, keep FEWER tool-result bodies verbatim (older huge DOMs collapse
    // to one-line stubs → smaller cached prefix → smaller 0.1×cacheRead growth per step). Tighter wins; a
    // user's explicit keepToolOutputs is never LOOSENED. Off until browserActive (normal coding untouched).
    const browserKeep = this.browserActive ? o.browserTrim?.keepOutputs : undefined;
    const keep = browserKeep != null
      ? (o.keepToolOutputs ? Math.min(o.keepToolOutputs, browserKeep) : browserKeep)
      : o.keepToolOutputs;
    if (keep) out = stubOldToolResults(out ?? m, keep);
    // Decide the image outcome BEFORE the token backstop, and budget on what will ACTUALLY be sent:
    // a stale ref weighs its ~50-byte stub (not the file it will never carry), and a tool-result ref
    // weighs the whole expanded base64 (an image Read could otherwise blow the window unbudgeted).
    // Orphan-stripping runs first so the expansion window sees the real message set.
    out = expandImagesForSend(dropOrphanToolResults(out ?? m), o.keepRecentImages ?? 1);
    if (o.maxContextTokens) {
      const pre = out.length;
      out = fitTokenBudget(out, o.maxContextTokens, o.keepRecentImages ?? 1);
      // Token-cap trims used to be SILENT — surface a one-line notice (deduped per drop count)
      // so the user knows context was auto-compacted, not mysteriously forgotten.
      const dropped = pre - out.length;
      if (dropped > 0 && dropped !== this.lastTrimNotified) {
        this.lastTrimNotified = dropped;
        o.host?.notify?.({ kind: 'compaction', message: `context full — auto-trimmed ${dropped} oldest message(s) to fit ~${Math.round(o.maxContextTokens / 1000)}k tokens` });
      }
    }
    // Unconditional correctness net: a tool_result whose tool_call parent isn't in the sent set is a
    // hard API 400 (INVALID_REQUEST). Orphans arise from compaction boundaries OR a resumed transcript
    // that was folded in a prior session — strip them no matter which knobs are on. Same ref if clean.
    // (fitTokenBudget can create new head orphans; it guards its own head, this catches the rest.)
    return dropOrphanToolResults(out);
  }
}

/** system (if any) + the most-recent (max - head) messages — pure drop-oldest. */
function dropOldest(m: Message[], max: number): Message[] {
  const head = m[0]?.role === 'system' ? [m[0]] : [];
  return [...head, ...m.slice(-(max - head.length))];
}

/** Anthropic's usable context without the `context-1m-2025-08-07` beta header (see Agent.contextCap). */
const ANTHROPIC_WINDOW_WITHOUT_1M_BETA = 200_000;
const isAnthropicModel = (m?: string): boolean => !!m && (m.startsWith('anthropic/') || /(^|\/)claude-/.test(m));

/** ~4 chars/token estimate over content + serialized tool_calls. THE estimator — the CLI footer,
 *  `/context` and `/compact` all route here so what the user sees and what compaction targets can't
 *  drift apart. Images are weighed AS SENT (`sendBytes`): only the recent window's refs cost their
 *  file, stale ones cost the stub. Budgeting a ref at full file size while the wire carries a 50-byte
 *  stub made `/compact` chase a target it could never reach and drop-oldest the conversation away. */
export function estimateTokens(m: Message[], keepRecentImages = 1): number {
  return Math.ceil(sendBytes(m, keepRecentImages) / 4);
}

/**
 * Note-taking: keep the most-recent `keep` tool-result messages verbatim; collapse older, bulky ones to a
 * one-line stub that still names the tool + its file (so the model can re-pull). Structure (role/tool_call_id/
 * name) is preserved so tool_use↔tool_result pairing stays valid; only `content` is replaced, on COPIES.
 */
/** Crop an oversized tool result to page 1 (cut at a line boundary) with a marker telling the model it
 *  was cropped and how to proceed. The default (lossy) handler when no capToolResult hook spills it. */
/**
 * Floor for a transport's per-tool-call deadline: an UNDECLARED tool is not a prompt tool, so the
 * transport must stay generous by default (this matches the cursor helper's own DEFAULT_TOOL_TIMEOUT_MS).
 */
export const TRANSPORT_TOOL_TIMEOUT_FLOOR_MS = 1_800_000;

/**
 * Layering assertion: every tool that declares how long it may run must finish strictly INSIDE the
 * transport deadline that wraps it. Returns one message per violation (empty = the layering holds).
 * Undeclared tools cannot be checked — which is why the deadline is floored, not derived from declarers.
 */
/**
 * Headroom a transport needs over ONE tool's declared bound.
 *
 * An ADVISORY bound (the default) is a tool's own intention, which it may overrun — a shell cap fires a
 * kill and then still has to reap, format and return — so the transport keeps generous headroom (x2 + 1m).
 * A SELF-ENFORCED bound (`maxDurationEnforced`, e.g. Task's wall clock, which aborts the child) cannot be
 * overrun, so doubling it only buys DEAD TIME: it doubles how long a genuinely hung call stays silent.
 * Modest slack for the return trip is enough. Either way the result strictly exceeds the tool's own bound,
 * which is the invariant `toolTimeoutViolations` asserts.
 */
/** An MCP-backed tool (eager `mcp__server__tool` or the deferred `McpCall`) — it can hand a slow call off to a job. */
const isMcpTool = (t: { name: string }) => t.name.startsWith('mcp__') || t.name === 'McpCall';

export function transportHeadroomMs(t: { maxDurationMs?: number; maxDurationEnforced?: boolean }): number {
  const d = Number(t.maxDurationMs) || 0;
  if (!d) return 0;
  return t.maxDurationEnforced ? d + 60_000 : d * 2 + 60_000;
}

export function toolTimeoutViolations(tools: Array<{ name: string; maxDurationMs?: number }>, transportMs: number): string[] {
  const out: string[] = [];
  for (const t of tools) {
    const d = Number(t.maxDurationMs) || 0;
    if (d && d >= transportMs) {
      out.push(`tool-timeout layering VIOLATED: '${t.name}' may run up to ${d}ms but the transport deadline is ${transportMs}ms — the transport wins the race and the tool's real result is destroyed. Raise providerOptions.toolTimeoutMs above ${d}ms.`);
    }
  }
  return out;
}

/**
 * FLOOR: no tool result reaches the transcript empty — ever, from any tool, for any reason.
 *
 * A blank is the single worst thing to show a model: it reads as "nothing happened" rather than "your
 * call failed", so the model neither retries meaningfully nor adapts — it re-issues the identical call.
 * (Real session 20260831-104225: six `{role:'tool', content:''}` entries from a shell command killed by
 * a transport timeout; the model re-ran the same unfinishable scan for nine turns and never finished.)
 *
 * The floor must not LIE, though. `tools:`/`addTools()` is a public API and a host tool may legitimately
 * return '' — telling the model that a tool which provably ran suffered a transport failure ("outcome is
 * UNKNOWN, retry it") invites a duplicate write, which is the same harm inverted. So the caller states how
 * the result was DELIVERED and the floor only makes the UNKNOWN claim when the result genuinely never
 * arrived:
 *   'ran'     — the tool returned; '' means it produced no output. Benign, and said so.
 *   'missing' — no result arrived (undefined output, a non-terminal status, a rejected/torn-down call).
 *   'error'   — the tool reported failure but gave no detail.
 */
export type ResultDelivery = 'ran' | 'missing' | 'error';

export function nonEmptyResult(out: string, name = 'tool', delivery: ResultDelivery = 'ran'): string {
  if (out.trim()) return out;
  if (delivery === 'error') return `[${name}] failed with no error message — the tool reported an error but produced no detail. Its effect is UNKNOWN: verify the state before assuming it did or did not happen.`;
  if (delivery === 'missing') {
    const base = `[${name}] returned no result. This is a transport failure, not an empty answer — the call's outcome is UNKNOWN, so do not assume it succeeded or failed.`;
    // The background escape hatch only exists for Shell; suggesting it for a Read/Grep would be noise.
    return name === 'Shell'
      ? `${base} Do NOT re-issue the same long-running command: run it with background:true and poll ShellOutput/ShellStatus, or narrow it so it can finish.`
      : `${base} Retry it, or verify the state another way before continuing.`;
  }
  return `[${name}] ran and returned no output. The call completed — this is an empty answer, not a failure.`;
}

/**
 * Does this tool output say NOTHING? The floor's real invariant is meaningful content, not a non-empty
 * string: a delegated `{}` / `[]` / an empty MCP `{content: []}` all stringify to something non-empty and
 * sail past a length check while teaching the model exactly as much as '' did. Scalars a tool deliberately
 * returned (a real `0`, a real `false`) ARE meaningful and are never vacuous.
 */
export function isVacuous(v: unknown, seen: Set<object> = new Set(), depth = 0): boolean {
  if (v == null) return true;
  if (typeof v === 'string') return v.trim() === '';
  if (typeof v !== 'object') return false; // number, boolean, bigint, symbol, function — a value the tool meant to return
  // Cycles and pathological nesting: the predicate runs OUTSIDE any try/catch on the transcript path, and
  // the line it replaced (JSON.stringify + catch) was cycle-tolerant. Neither is "nothing", so both are
  // non-vacuous rather than a RangeError out of transcript assembly.
  if (seen.has(v) || depth >= 64) return false;
  seen.add(v);
  try {
    if (Array.isArray(v)) return v.every((x) => isVacuous(x, seen, depth + 1));
    // Only PLAIN objects can be judged by their own enumerable values. A Date, Map, Set, Error, Buffer or
    // class instance carries its content somewhere `Object.values` cannot see — `Object.values(new Date())`
    // is `[]`, which would floor a real timestamp away as "ran and returned no output".
    const proto = Object.getPrototypeOf(v);
    if (proto !== Object.prototype && proto !== null) return false;
    const vals = Object.values(v as Record<string, unknown>);
    return vals.length === 0 || vals.every((x) => isVacuous(x, seen, depth + 1));
  } finally {
    seen.delete(v);
  }
}

/**
 * Normalize a delegated runtime's tool output into the same `{text, images}` shape `dispatch()` returns,
 * so the reconstructed transcript record can carry pictures instead of a wall of base64 text.
 *
 * Two image-bearing shapes reach here, because a host tool's result makes a ROUND TRIP on the cursor path:
 * the toolExecutor hands back dispatch()'s `{text, images}`, the helper converts it to the SDK's content
 * blocks (`{content:[{type:'text'},{type:'image',data,mimeType}]}`, see ai.libx.js cursor-tool-result.ts),
 * and it is the SDK's echo of THAT which lands on the completed event as `output`. Accept both, plus a
 * bare block array (the MCP shape) — none of them should degrade into stringified base64.
 */
export function delegatedResult(output: unknown): { text: string; images: Array<{ mimeType: string; data: string }> } {
  if (typeof output === 'string') return { text: output, images: [] };
  if (output == null) return { text: '', images: [] };
  const isImg = (i: any) => i && typeof i.data === 'string' && typeof i.mimeType === 'string';
  const o = output as { text?: unknown; images?: unknown; content?: unknown };
  if (Array.isArray(o.images)) {
    const images = (o.images as any[]).filter(isImg);
    if (images.length) return { text: typeof o.text === 'string' ? o.text : '', images };
  }
  const blocks = Array.isArray(output) ? output : Array.isArray(o.content) ? o.content : undefined;
  if (blocks) {
    const images = (blocks as any[]).filter((b) => b?.type === 'image' && isImg(b)).map((b: any) => ({ mimeType: b.mimeType, data: b.data }));
    if (images.length) {
      const text = (blocks as any[]).filter((b) => b?.type === 'text' && typeof b.text === 'string').map((b: any) => b.text).join('\n');
      return { text, images };
    }
  }
  try { return { text: JSON.stringify(output), images: [] }; } catch { return { text: String(output), images: [] }; }
}

/** How a delegated runtime's reconstructed tool record was delivered (see nonEmptyResult). */
export function deliveryOf(output: unknown, status?: string): ResultDelivery {
  if (status === 'error') return 'error';
  if (output == null) return 'missing';                       // never arrived (cursor's `output: undefined`)
  if (status != null && status !== 'completed') return 'missing'; // still running / torn down mid-call
  return 'ran';
}

export function cropResult(result: string, cap: number): string {
  const head = result.slice(0, cap);
  const nl = head.lastIndexOf('\n');
  const page = nl > cap * 0.5 ? head.slice(0, nl) : head; // prefer a clean line cut if not too early
  const omitted = result.length - page.length;
  return `${page}\n\n[output cropped — showing ${page.length} of ${result.length} bytes; ${omitted} omitted. ` +
    `This is page 1. Refine your query/command to narrow it, or call the tool again with a tighter scope to see more.]`;
}

function stubOldToolResults(messages: Message[], keep: number): Message[] {
  const meta = new Map<string, string | undefined>(); // tool_call_id -> path arg (for the pointer)
  for (const msg of messages)
    for (const tc of msg.tool_calls ?? []) {
      let path: string | undefined;
      try { const a = tc.function.arguments ? JSON.parse(tc.function.arguments) : {}; if (typeof a.path === 'string') path = a.path; } catch { /* args not JSON */ }
      meta.set(tc.id, path);
    }
  const toolIdx = messages.map((x, i) => (x.role === 'tool' ? i : -1)).filter((i) => i >= 0);
  const stub = new Set(toolIdx.slice(0, Math.max(0, toolIdx.length - keep)));
  if (stub.size === 0) return messages;
  return messages.map((x, i) => {
    const text = contentText(x.content);
    if (!stub.has(i) || text.length <= 120) return x; // small results aren't worth stubbing
    const where = meta.get(x.tool_call_id ?? '');
    const lines = text.split('\n').length;
    return { ...x, content: `[${x.name ?? 'tool'}${where ? ` ${where}` : ''} output elided — ${lines} lines; re-run the tool to view]` };
  });
}

/**
 * Elide oversized message BODIES until the transcript fits `target`. Progressive caps: shave the
 * biggest first, tighten only if still over. Tool results are the usual whales, but a single 1MB user
 * paste is shortened in place too — it must never fall through to the drop-oldest backstop and take the
 * user's own text with it. Only `content` changes, on copies — role/tool_call_id/name stay intact so
 * tool_use↔tool_result pairing holds; the system head is never touched.
 */
/** Drop inline base64 images — heaviest message first — until the transcript fits `target`. Unlike text,
 *  an image part can't be shortened, so an oversized screenshot must be elided (to `IMAGE_ELIDE_STUB`)
 *  BEFORE the text ladder starts paying for it by shredding, then dropping, the conversation itself.
 *  Stops as soon as we're under budget, so a small/recent image survives when text is the real weight. */
function elideOversizedImages(messages: Message[], target: number, protectNewest = false, keepRecentImages = 1): Message[] {
  let out = messages;
  // REF-AWARE: post-image-refs every image in a live transcript is a ref, so selecting on inline
  // base64 alone made both ladder passes a no-op (and `protectNewest` protect nothing) — the ladder
  // then paid for the image by shredding, and finally dropping, the user's text.
  const withImages = out.map((_, i) => i).filter((i) => messageHasImage(out[i].content));
  // `protectNewest` holds back the most recent image-bearing message — the one `releaseStaleImages`
  // guarantees survives; the caller re-runs unprotected only if the text ladder still can't fit us.
  const newest = protectNewest ? withImages[withImages.length - 1] : -1;
  // Order by WIRE weight, not stored weight: a stale ref already weighs the stub, so eliding it frees
  // nothing — shave the in-window image that actually costs the file first.
  const weight = sendBytesPer(out, keepRecentImages);
  const heaviestFirst = withImages
    .filter((i) => i !== newest)
    .sort((a, b) => weight[b] - weight[a]);
  for (const i of heaviestFirst) {
    if (estimateTokens(out, keepRecentImages) <= target) break;
    const c = elideImages(out[i].content!);
    if (c === out[i].content) continue;
    out = out.map((m, j) => (j === i ? { ...m, content: c } : m));
  }
  return out;
}

function capOversizedBodies(messages: Message[], target: number, keepRecentImages = 1): Message[] {
  let out = messages;
  for (const cap of [8000, 4000, 2000, 800, 300, 120]) {
    if (estimateTokens(out, keepRecentImages) <= target) break;
    out = out.map((x) => (x.role === 'system' ? x : elideBody(x, cap)));
  }
  return out;
}

/** Elide one message's text down to `cap` chars. Array content keeps its non-text parts (images)
 *  intact — only the text parts are shaved — and the note says so, rather than dropping them invisibly. */
function elideBody(x: Message, cap: number): Message {
  const note = (bytes: number, imgs: number) =>
    `\n\n[${bytes} bytes elided by /compact${x.role === 'tool' ? ' — re-run the tool to view' : ''}` +
    `${imgs ? `; ${imgs} image part(s) KEPT in full — they still cost context` : ''}]`;
  if (!Array.isArray(x.content)) {
    const text = contentText(x.content);
    if (text.length <= cap) return x;
    return { ...x, content: `${text.slice(0, cap)}${note(text.length - cap, 0)}` };
  }
  const imgs = x.content.filter((p) => p.type !== 'text').length;
  let left = cap;
  let elided = 0;
  // EMPTY text parts are dropped, never emitted: once `left` hits 0 every later text part would otherwise
  // become `{type:'text', text:''}` — which the Anthropic API rejects outright.
  const parts: ContentPart[] = [];
  for (const p of x.content) {
    if (p.type !== 'text') { parts.push(p); continue; }
    const t = p.text ?? '';
    if (t.length <= left) { left -= t.length; if (t) parts.push(p); continue; }
    const keep = left; left = 0; elided += t.length - keep;
    if (keep > 0) parts.push({ ...p, text: t.slice(0, keep) });
  }
  // Nothing shaved: only rewrite if we actually dropped pre-existing empty parts (never to an empty body).
  if (!elided) return parts.length === x.content.length || !parts.length ? x : { ...x, content: parts };
  // The note is always appended, so the message can never end up with zero content parts.
  return { ...x, content: [...parts, { type: 'text', text: note(elided, imgs).trimStart() }] };
}

/** All tool_call ids present on assistant messages — one O(n) pass instead of a scan per result. */
const callIdSet = (msgs: Message[]): Set<string> => {
  const ids = new Set<string>();
  for (const m of msgs) if (m.role === 'assistant') for (const tc of m.tool_calls ?? []) ids.add(tc.id);
  return ids;
};

/** Drop every orphan tool_result (one whose tool_call isn't in the set) — sending one is a hard API
 *  400. Anywhere in the sequence, not just the head. Returns the same array reference when clean. */
function dropOrphanToolResults(messages: Message[]): Message[] {
  const ids = callIdSet(messages);
  const ok = (m: Message) => m.role !== 'tool' || ids.has(m.tool_call_id ?? '');
  return messages.every(ok) ? messages : messages.filter(ok);
}

/**
 * Token-aware backstop: keep the system head, drop oldest body messages until under `maxTokens`.
 * The window may empty entirely (a single message can exceed the whole budget), and it is never
 * left starting on an ORPHAN tool_result (one whose tool_call isn't in the window) — that would
 * break tool_use↔tool_result pairing and get rejected by the API. If even [system] is over budget
 * (e.g. a huge memory index), we can't trim further — warn; the hard `maxTokens` kill-switch is the real guard.
 */
function fitTokenBudget(messages: Message[], maxTokens: number, keepRecentImages = 1): Message[] {
  // Incremental accounting: estimate each message once, subtract as we drop — O(n), not O(n²)
  // (re-estimating the whole window per dropped message walks every char repeatedly). Per-message
  // weights come from the SHARED window (`sendBytesPer`), so a stale ref isn't charged its file.
  const per = sendBytesPer(messages, keepRecentImages).map((b) => Math.ceil(b / 4));
  let total = per.reduce((a, b) => a + b, 0);
  if (total <= maxTokens) return messages;
  const head = messages[0]?.role === 'system' ? [messages[0]] : [];
  let from = head.length; // body = messages[from..]
  while (from < messages.length && total > maxTokens) total -= per[from++];
  const ids = callIdSet(messages.slice(from));
  while (from < messages.length && messages[from].role === 'tool' && !ids.has(messages[from].tool_call_id ?? '')) total -= per[from++];
  if (total > maxTokens)
    log.warn(`context ~${total} tok still over maxContextTokens=${maxTokens} after trimming (system head can't be dropped)`);
  return [...head, ...messages.slice(from)];
}

/** system (if any) + synthetic summary of the dropped middle + most-recent window; fits within `max`. */
function compact(m: Message[], max: number, focus?: string): Message[] {
  const hasSystem = m[0]?.role === 'system';
  const head = hasSystem ? [m[0]] : [];
  // Reserve one slot for the summary, the rest for the most-recent window. Bound the tail
  // to the post-head slice so a short transcript (max >= length) can't re-include the head.
  const tailCount = Math.max(1, max - head.length - 1);
  const tail = m.slice(head.length).slice(-tailCount);
  const dropped = m.slice(head.length, m.length - tail.length);
  if (dropped.length === 0) return [...head, ...tail];
  return [...head, { role: 'system', content: summarize(dropped, focus) }, ...tail];
}

/** Header of a synthetic recap produced by `summarize` — how a LATER pass recognizes its own output. */
const SUMMARY_HEAD = '[summary of ';
/** Upper bound on the message count a single prior recap may claim (untrusted input — see summarize). */
const MAX_CARRIED_COUNT = 10_000;
const isSyntheticSummary = (m: Message): boolean =>
  m.role === 'system' && typeof m.content === 'string' && m.content.startsWith(SUMMARY_HEAD);

/** Lightweight, deterministic, LLM-free recap of a span of messages.
 *  A recap from an EARLIER pass inside the span is carried forward (its message count and its detail
 *  lines), never silently thrown away — the escalation ladder re-folds an already-folded transcript, and
 *  a recap that discards its predecessor would lie about how much history it stands for. */
function summarize(messages: Message[], focus?: string): string {
  const tools = new Set<string>();
  const files = new Set<string>();
  const carried: string[] = []; // detail lines from prior recaps in this span (deduped, bounded)
  let carriedCount = 0; // messages those recaps already stood for
  let priors = 0;
  let lastAssistant = '';
  for (const msg of messages) {
    if (isSyntheticSummary(msg)) {
      priors++;
      const [head, ...rest] = String(msg.content).split('\n');
      // Clamp: a recap head is data, not a promise — a restored session or a hook-written system message
      // could carry `[summary of 999999 …]` and we must not absorb it verbatim.
      carriedCount += Math.min(Math.max(Number(/of (\d+)/.exec(head)?.[1]) || 0, 0), MAX_CARRIED_COUNT);
      for (const l of rest) if (l.trim() && !carried.includes(l) && carried.length < 20) carried.push(l);
      continue;
    }
    for (const tc of msg.tool_calls ?? []) {
      tools.add(tc.function.name);
      try {
        const args = tc.function.arguments ? JSON.parse(tc.function.arguments) : {};
        if (typeof args.path === 'string') files.add(args.path);
      } catch {
        /* arguments not JSON — skip file extraction */
      }
    }
    if (msg.role === 'assistant' && msg.content) lastAssistant = contentText(msg.content);
  }
  const lines = [`${SUMMARY_HEAD}${messages.length - priors + carriedCount} earlier message(s)]`];
  if (tools.size) lines.push(`tools used: ${[...tools].join(', ')}`);
  if (files.size) lines.push(`files touched: ${[...files].join(', ')}`);
  if (lastAssistant) lines.push(`last assistant: ${lastAssistant}`);
  if (carried.length) lines.push('earlier recap:', ...carried.map((l) => `  ${l}`));
  // Focused compaction: keep lines from the dropped span that mention any focus keyword,
  // verbatim (bounded), so "/compact keep the API details" doesn't lose them to the recap.
  if (focus?.trim()) {
    const words = focus.toLowerCase().split(/\s+/).filter((w) => w.length > 2);
    const kept: string[] = [];
    // THIS span's own messages are scanned FIRST: a prior recap sits at index 0 and would otherwise eat the
    // whole budget, so repeated `/compact <focus>` would keep only the OLDEST hits and drop the newest.
    // (Older hits still reach the recap via `carried` above.)
    const ordered = [...messages.filter((m) => !isSyntheticSummary(m)), ...messages.filter(isSyntheticSummary)];
    outer: for (const msg of ordered) {
      if (msg.role === 'tool') continue; // tool bodies are bulk noise; user/assistant text carries the decisions
      for (const line of contentText(msg.content).split('\n')) {
        const l = line.toLowerCase();
        if (line.trim() && words.some((w) => l.includes(w))) {
          kept.push(line.trim().slice(0, 200));
          if (kept.length >= 12) break outer;
        }
      }
    }
    if (kept.length) lines.push(`preserved (re: ${focus.trim()}):`, ...kept.map((l) => `  ${l}`));
  }
  return lines.join('\n');
}

function lastAssistantText(messages: Message[]): string {
  for (let i = messages.length - 1; i >= 0; i--) {
    if (messages[i].role === 'assistant') return contentText(messages[i].content);
  }
  return '';
}

/** Canonical ledger key for a tool name: case-insensitive, with delegated-runtime aliases folded
 *  (cursor reports `shell`/`read`, agentx advertises `Shell` — or `Bash` when a host renames it). */
const TOOL_ALIASES: Record<string, string> = { bash: 'shell' };
function toolKey(name: string): string {
  const k = name.toLowerCase();
  return TOOL_ALIASES[k] ?? k;
}
